/**
 * Routes button clicks and actions to appropriate handlers
 * Extensible registry pattern for easy addition of new actions
 */
import { API } from '../core/api.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import page from '../vendor/page.js';
import {
  clearAllAdviceTerminusPills,
  hasRenderedAdviceTakeaway,
  ADVICE_TERMINUS_PILL_GROUP_IDS
} from '../core/adviceTerminusPills.js';
import {
  ADVICE_CREATE_MENU_GROUP_ID,
  refreshAdviceCreateMenu,
  resolveAdviceCreateMenuItems
} from '../core/adviceCreateMenu.js';
import { emitContentActivated } from '../core/contentSync.js';
import { resolveSharingDeclineMessage } from './foundationCompletion.js';

// Body of the post-intake handoff message. Streamed by the close_intake
// handler after the agent's acknowledgment turn. Frames the two paths
// forward — Continue Discovery (build out the broader picture) vs. Start a
// conversation (talk about what was just shared right away).
const POST_INTAKE_BODY =
  "You've got two good paths from here.\n\n" +
  "We can continue with **Starting Your Journey** to deepen our understanding across different areas of your relationship. We'll learn about your attachment style, emotional intimacy, and core personality traits. About 20 minutes to finish.\n\n" +
  "Or, if you prefer, we can start a conversation about what you just shared and dig in right away. Either way your progress is saved and you can always come back to the other later from your dashboard page.";

// Fired by the "Skip for now" button on the intake-launch gate. Pure UI
// transition — no INTAKE session has started, no LLM call, no persistence.
// The orchestrator's prerequisiteIndex advancement (via _enterFoundation
// with initialAction: 'prerequisite_completed') is itself the permanent
// skip: once the index moves past intake, Foundation re-entry routes
// straight to SECTIONS and never re-presents the gate.
const SKIP_INTAKE_MESSAGE =
  "No problem — you can bring it up anytime in a conversation. Let's continue.";

// Bridge bubble shown the instant the user clicks "Let's build an experiment"
// (Advice → Treatment:BUILD). The real BUILD turn is slow to first token: it
// runs a session switch, then a Sonnet tool loop (getPracticeBlueprint →
// createPractice) before any text. Without this the assistant area sits blank
// for several seconds. Frontend-only, not persisted, never seen by the LLM
// (same contract as SKIP_INTAKE_MESSAGE).
const BUILD_HANDOFF_BRIDGE_MESSAGE =
  "Great — let's build this into an experiment. Give me a moment to put it together.";

// Frontend-only bridge bubble shown while the Treatment → Advice handoff
// extraction runs (covers the Haiku latency so the user never sees a blank gap).
// Not persisted, never seen by the LLM.
const ADVICE_HANDOFF_BRIDGE_MESSAGE =
  "Let's pick this up in a conversation — one moment.";

// Action types whose handler sends its OWN user message in place (via
// handleUserMessage, which both renders the bubble and runs the assistant turn).
// These must never echo: their group may be flagged `echo: true` for the
// navigate-away siblings, but echoing here would render a SECOND user bubble —
// live (_maybeEchoButtonClick) and again on reload (recordButtonInteraction).
const SELF_SENDING_ACTIONS = new Set([
  // Partner-memo generate/refine handlers call handleUserMessage themselves
  // (rendering their own user bubble). The partner-memo-review group is echo:true
  // for the send/discard siblings, so these must be excluded or they'd double-bubble.
  'generate_partner_memo',
  'generate_partner_memo_reply',
  'memo_refine_warmer',
  'memo_refine_shorter',
  // compose_practice sends its own user message → the advice agent (re-)calls the
  // composePractice tool. Its button groups are echo:true for their decline siblings,
  // so it must be excluded here or it would double-bubble.
  'compose_practice',
  // The "Sum this up" wrap-up pill sends its own visible request for the Key
  // Takeaways card. Its group is echo:false, so this entry is documentation of
  // the contract rather than the mechanism that prevents the double bubble.
  'request_takeaways',
  // Advice cascade (Phase C): "Not now" fires its own silent [cascade_declined:*]
  // marker.
  'cascade_decline',
  // Terminus recommendation flow: the memo-lead "Not now" fires its own silent
  // [recommendation_declined] marker, exactly like `cascade_decline`. Its catalog
  // button carries no `echo` flag, so this is the same belt-and-braces contract
  // entry rather than the mechanism suppressing a bubble.
  //
  // Deliberately ABSENT: `request_recommendation`, `request_deep_mode`, and
  // `choose_direction`. Their markers are SILENT (`silent: true` → ModeOrchestrator
  // renders no bubble), so the catalog's `echo: true` is what puts the clicked
  // label in the transcript — listing them here would resolve echo to false and
  // make the click invisible both live and on reload. Precedent:
  // `accept_practice`/`reject_practice`, which fire silent markers from an
  // `echo: true` group and are likewise absent.
  'recommendation_decline'
  // The report conversation's continue terminus (`close_practice` /
  // `keep_open_practice`, and their retired `continue_practice` /
  // `set_aside_practice` predecessors) is NOT here: its group is echo:true like
  // its practice-completion-decision sibling, and each handler's own silent
  // [practice_close_requested]/[practice_continued] marker is a separate
  // LLM-context send after the echo, not a self-rendered bubble — including
  // them here would suppress the echo.
]);

/**
 * Error code returned by `POST /chat/button-interaction` (409) when a Create-menu
 * item is no longer offerable — the couple dissolved, the artifact was already
 * taken, or an intent is pending. The menu was rendered from a projection that
 * has since moved; recover by refreshing it, not by reporting a failure.
 * @type {string}
 */
const CREATE_MENU_STALE_CODE = 'CREATE_MENU_ITEM_UNAVAILABLE';

/**
 * Error code returned by `POST /chat/button-interaction` (409) when an
 * experiment-direction slot is no longer pickable — the 8-message detour window
 * closed, the candidate list moved, or a proposal is already live. Keyed on the
 * CODE, not the HTTP status: `recordButtonInteraction` returns an error envelope
 * and the status is not preserved along that call path.
 * @type {string}
 */
const DIRECTIONS_STALE_CODE = 'EXPERIMENT_DIRECTIONS_UNAVAILABLE';

/**
 * The classic post-takeaway button group for the current user — the terminal
 * fall-through the server's `selectPostTakeawayGroup` lands on once the
 * recommendation path is spent. Used to recover from a stale directions click:
 * re-arming the directions group itself would 409 again on the next click.
 *
 * Coupling is read from the boot-time `coupleId` snapshot (the same signal
 * `composeNoteLauncher` gates the memo entry point on) — the client has no other
 * coupling fact, and the server's own group choice is not echoed back on the 409.
 *
 * @returns {string} `advice-takeaway-standard-coupled` | `…-uncoupled`
 */
function classicPostTakeawayGroupId() {
  return window.app?.currentUser?.coupleId
    ? 'advice-takeaway-standard-coupled'
    : 'advice-takeaway-standard-uncoupled';
}

// ADVICE artifact + decline actions whose acceptance boundary is the durable
// `POST /chat/button-interaction` transaction (advice-unified-terminus §3.3).
// For these, in an ADVICE session, a FAILED interaction record aborts the handler
// and restores the pill — the obligation was never accepted server-side. Every
// other button keeps the lightweight fail-open recording. Correction pills are
// deliberately absent (their acceptance boundary is `POST /chat/set-stance`).
const CONTRACT_BEARING_ADVICE_ACTIONS = new Set([
  'compose_practice',
  'generate_partner_memo',
  'generate_partner_memo_reply',
  'cascade_decline',
  // Terminus recommendation flow: a direction pick opens an artifact intent and
  // writes `selectedPracticeCode` server-side, so the interaction record IS the
  // acceptance boundary — and it is what surfaces the stale-pick 409. Matched on
  // the bare prefix (`executeAction` keys the set on `action.split(':')[0]`), same
  // as `cascade_decline`, so all three `choose_direction:<n>` slots are covered.
  'choose_direction'
]);

// Group ids whose generate_partner_memo click is a READINESS signal inside a
// memo_compose session (the standing pill + the agent-posed convergence offer),
// as opposed to a fresh request from the Create menu or a post-takeaway offer.
const MEMO_COMPOSE_READINESS_GROUP_IDS = new Set(['partner-memo-offer', 'advice-memo-compose-offer']);

// Button-group ids whose decision carries durable server-side proposal state
// (`memoProposalId` / `practiceProposalState === 'proposed'`) that outlives the
// button click itself. A typed detour submitted while one of these is stashed
// behind the textarea must not destroy the stash — the draft would still be
// live server-side but become invisible/unreachable in the UI (app-tools plan
// Phase 3.5, decision #8). Consulted by `ModeOrchestrator._applyStashDropGuard`
// via `Input.getOwningButtonGroupId()`.
export const DURABLE_PROPOSAL_GROUP_IDS = new Set([
  'partner-memo-review',
  'practice-proposal-decision',
  // Terminus recommendation flow: the three direction slots are backed by
  // candidates persisted on the session and stay legal for a fixed 8-message
  // detour window, so a typed detour must SUPPRESS the stash, not destroy it —
  // the pick is still live server-side. (`advice-takeaway-teaser` is deliberately
  // absent: it carries no server-side state a detour could orphan, and the rearm
  // projection re-mounts it from `recommendationDeliveredAtMessageCount` being
  // absent, so dropping its stash loses nothing. Same ruling for the deep-mode
  // variants `advice-takeaway-teaser-deep` and `advice-recommend-deep`: neither
  // carries durable server-side state either — `request_deep_mode` stamps
  // `deepModeEnteredAtMessageCount` only on click, not on mount, so there is
  // nothing yet to orphan.)
  'advice-experiment-directions'
]);

// Actions a GUEST may click but not complete — each opens the account-creation
// overlay instead of running, and the click is continued verbatim once the
// account exists. Values are the overlay copy context (CONVERSION_OVERLAY_COPY).
//
// These are the demo's outcomes, not its content: the guest gets the whole
// orientation advice conversation, and the ask lands only when they reach for
// something that outlives it. Account holders never see this path.
const GUEST_CONVERSION_GATED_ACTIONS = new Map([
  ['orientation_to_foundation', 'conclusion_foundation'],
  ['orientation_exit', 'conclusion_exit'],
  ['switch_orientation_scope', 'offtopic_expand']
]);

// `navigateTo` target→route table (app-tools plan Phase 2, D2). The backend
// enum lives in `actionTools.js`'s `NAVIGATE_TO_TARGETS` (ACTION_TOOL_MAP
// `navigateTo` entry) — `frontend-user` cannot import `backend/src`, so this
// is necessarily a SECOND, hand-maintained list. Pinned to the backend enum
// by a direct cross-package import test
// (`frontend-user/test/orchestration/navigateTo.sync.test.js`), the same
// precedent as `frontend-user/test/constants/attachments.test.js`. Tab
// destinations route through the merged Library page (`/library?tab=…`) —
// `/insights` is a legacy client-side redirect (`router.js`), never the
// canonical target.
export const NAVIGATE_TO_ROUTES = {
  patterns: '/library?tab=patterns',
  guides: '/library?tab=guides',
  assessments: '/library?tab=assessments',
  practices: '/experiments',
  partner_notes: '/notes',
  upgrade: '/upgrade',
  subscription_manage: '/subscription'
};

// Targets whose backing endpoint is `requireAuth` with no guest fallback
// (verified: `GET /practices` in practices.js, `GET /memos` in
// discoveryCouples.js, `GET /patterns` in patterns.js, `GET /guides` in
// guides.js — all plain `requireAuth`, not `requireAuthOrGuest`; without the
// gate a guest lands on the library's dead-end "Please try again" error card).
// `assessments` is deliberately NOT gated: its endpoint
// (`GET /discovery/frameworks/results`) is `requireAuthOrGuest`, and a guest
// who completed the orientation Love-Languages questionnaire has a real
// result there — gating would block them from their own content.
// navigateTo's target SET is not narrowed for guests (decision #2 — "same
// target set"); this only decides whether arrival opens the conversion gate
// first, so a guest never lands on a raw fetch-error page.
// `subscription_manage` is gated for the same reason (guests cannot hold a
// subscription); for authenticated free users the `/subscription` page's own
// tierState logic already redirects to `/upgrade`, so no tier check is needed here.
const NAVIGATE_TO_AUTH_GATED_TARGETS = new Set(['practices', 'partner_notes', 'patterns', 'guides', 'subscription_manage']);

/**
 * Resolve the current active partner's display name for a synthetic user message.
 * The lookup is deliberately fresh so an old component cache cannot name a former
 * partner after a disconnect. Fail open to generic copy when the lookup is unavailable.
 *
 * @returns {Promise<string|null>} Linked partner name, or null when unavailable
 */
async function getLinkedPartnerName() {
  try {
    const result = await new DiscoveryAPI().getPartner();
    const partner = result?.data;
    const name = partner?.firstName || partner?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : null;
  } catch {
    return null;
  }
}

// Actions whose feature surface has been retired (focus areas /
// issue-driven-therapy, removed 2026-07). Old transcripts persist forever, so
// buttons carrying these actions can still be clicked on reloaded history —
// they must degrade to a friendly notice, not an "Unknown action" error.
// Matched on the action's base type (the part before the first ':').
const RETIRED_ACTIONS = new Set([
  'focus_area_open_chat',
  'focus_area_start_discovery',
  'focus_area_practice_checkin',
  'focus_area_begin_session',
  'pull_thread',
  'navigate_to_focus_areas',
  'issue_match',
  'discuss_extracted_focus',
  'work_on_focus_area_from_onboarding',
  'intake_return_to_focus_areas',
  'reflect_resolve',
  'resolve_issue',
  'confirm_issue_resolution',
  'decline_issue_resolution',
  'changed_shape_issue_resolution'
]);

// Retired `switch_mode` TARGETS — matched on the target mode (the segment after
// `switch_mode:`), not on the action's base type. INSIGHT is the first
// retirement (insight-mode-retirement-and-advice-migration.md): a replayed
// historical button (e.g. from a stale nav card render, or a cached client)
// lands on the degrade below instead of minting a session in a retired mode.
// Exported so tests (and any future retirement) can seed it.
export const RETIRED_SWITCH_TARGETS = new Set(['INSIGHT']);

export class ActionRouter {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;
    this.handlers = new Map();

    // Last `show_buttons` input per groupId. A re-arm is a re-render of a group
    // that was already mounted with dynamic input (practiceId, the direction
    // slot count/labels, …), and re-arming with `{}` silently drops all of it —
    // so the pre-acceptance-failure retry path reads its input back from here.
    // Written by the `show_buttons` handler itself, which every re-arm routes
    // through, so the memo always reflects what is actually on screen.
    this._lastGroupInput = new Map();

    // Register default handlers
    this.registerDefaultHandlers();
  }

  registerDefaultHandlers() {
    // Mode switching
    this.register('switch_mode', async (data) => {
      const [, modeName, subMode] = data.action.split(':');
      // The model-facing switchMode tool has subMode optional, but the backend
      // now 400s a TREATMENT creation without one (INVALID_SUBMODE). Default a
      // bare TREATMENT target to 'build' — same resolution the dashboard
      // intent-detection path applies (ModeOrchestrator TREATMENT default).
      const effectiveSubMode = subMode || (modeName === 'TREATMENT' ? 'BUILD' : null);
      const transitionData = effectiveSubMode ? { ...data, subMode: effectiveSubMode.toLowerCase() } : data;

      // Per-target retirement degrade. This CANNOT live in `RETIRED_ACTIONS`:
      // executeAction strips params (`action.split(':')[0]` → 'switch_mode') and
      // consults that set only under `if (!handler)` — structurally unreachable
      // while `switch_mode` has a registered handler. So the target check runs
      // here, after parsing and before the ADVICE guard, and returns without
      // falling through to any switch branch or backend call.
      if (RETIRED_SWITCH_TARGETS.has(modeName)) {
        console.log(`[ActionRouter] Retired switch_mode target clicked: ${modeName}`);
        window.notificationManager?.showInfo('This feature has been retired.');
        return;
      }

      // ADVICE exposes switchMode only for Discovery. The model-facing schema is
      // guidance rather than a hard boundary, so enforce the allowlist here before
      // any directed or generic switch branch can execute a malformed tool call.
      if (this.orchestrator.currentMode?.name === 'ADVICE' && modeName !== 'DISCOVERY') {
        console.warn('[ActionRouter] Rejected disallowed ADVICE mode switch', {
          requestedMode: modeName || null,
          requestedSubMode: subMode || null,
          action: data.action
        });
        return;
      }

      // Foundation → Advice bridge: user clicked "Start a conversation" on the
      // foundation-complete card. Propagate entrySource into the session metadata,
      // skip the generic ADVICE welcome ("What's on your mind?"), and dispatch
      // a silent `[begin]` so the strategy's first-turn branch generates the
      // Sonnet bridge opener (pattern matches intake silent opener).
      if (modeName === 'ADVICE' && data.data?.entrySource === 'foundation_complete') {
        await this.orchestrator.switchMode('ADVICE', {
          ...transitionData,
          entrySource: 'foundation_complete',
          skipWelcome: true
        });
        await this.orchestrator.handleUserMessage('[begin]', {
          metadata: {
            source: 'button_click',
            silent: true,
            systemTrigger: true,
            skipCounting: true,
            entrySource: 'foundation_complete'
          }
        });
        return;
      }

      // Directed handoff: Advice → Discovery
      // Discovery uses orchestrator loop for automated path selection + flow
      if (modeName === 'DISCOVERY') {
        await this.orchestrator.switchMode('DISCOVERY', { ...transitionData, skipWelcome: true });
        await this.orchestrator._enterDiscoveryNavigation(null);
        return;
      }

      // Directed handoff: Advice → Treatment:BUILD
      if (modeName === 'TREATMENT' && subMode === 'BUILD') {
        // Immediate feedback: the BUILD turn is slow to first token (session
        // switch + Sonnet tool loop). Stream a short bridge bubble and keep the
        // thinking indicator up so the user never sees a blank assistant area.
        // MessageRenderer.finish() deactivates the indicator when the bridge
        // bubble settles, so re-activate it afterward to cover the switch/
        // tool-loop window until real text streams.
        const chatManager = this.orchestrator.chatManager;
        chatManager.activateThinkingIndicator();
        await new Promise(resolve => setTimeout(resolve, 300));
        await chatManager.streamProgrammaticMessage(BUILD_HANDOFF_BRIDGE_MESSAGE, {
          chunkDelay: 400,
          messageType: 'system'
        });
        // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
        // Post-bridge re-arm: every branch from here ends in handleUserMessage
        // → SSE (switchMode renders no welcome for TREATMENT), so the logo+text
        // covers the session switch + tool-loop TTFB.
        chatManager.activateThinkingIndicator(undefined, { waiting: true });

        this._sourceSessionId = null;
      }

      // Directed handoff: Treatment → Advice (LLM-direct switchMode only).
      // The agent decided the user's message belongs in open conversation. Mirror the
      // reverse (Advice→Build) bridge: extract a thread + the verbatim ask from the
      // source session, seed the new advice session, and forward the message so it's
      // actually answered with continuity — instead of a cold "What's on your mind?".
      // Gated to LLM-direct switches (`!data.label`) from a conversational source mode;
      // button `switch_mode:ADVICE` (nav / mode-switch) stays a deliberate fresh start.
      // Snapshot source mode + session BEFORE switchMode mutates currentMode/sessionId.
      const adviceSourceMode = this.orchestrator.currentMode?.name;
      const adviceSourceSessionId = this.orchestrator._getSessionId();
      if (
        modeName === 'ADVICE'
        && !data.label
        && adviceSourceMode === 'TREATMENT'
        && adviceSourceSessionId
      ) {
        const chatManager = this.orchestrator.chatManager;
        chatManager.activateThinkingIndicator();
        await new Promise(resolve => setTimeout(resolve, 300));
        await chatManager.streamProgrammaticMessage(ADVICE_HANDOFF_BRIDGE_MESSAGE, {
          chunkDelay: 400,
          messageType: 'system'
        });
        // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
        // Post-bridge re-arm: the forward branch ends in handleUserMessage → SSE
        // (skipWelcome:true suppresses the ADVICE welcome); the no-message branch
        // deactivates before its static welcome, so the logo can't strand.
        chatManager.activateThinkingIndicator(undefined, { waiting: true });

        // handleResponse() unwraps { success, data } → returns the payload directly.
        let handoff = null;
        try {
          handoff = await API.getAdviceHandoff(adviceSourceSessionId);
        } catch (error) {
          console.warn('[ActionRouter] Advice handoff extraction failed:', error);
        }

        if (handoff?.lastUserMessage) {
          // A genuine human message to answer → bypass SCOPING (attunementCompletedAt:0),
          // carry the thread for continuity, suppress the static welcome.
          transitionData.entryType = 'mode_handoff';
          transitionData.attunementCompletedAt = 0;
          transitionData.skipWelcome = true;
          if (handoff.conversationThread) {
            transitionData.handoffContext = { conversationThread: handoff.conversationThread };
          }
          try {
            await this.orchestrator.switchMode('ADVICE', transitionData);
          } catch (error) {
            chatManager.deactivateThinkingIndicator();
            throw error;
          }
          // Forward the verbatim ask SILENTLY: the user's question bubble persists in the
          // chat view across the switch (switchMode does not clear it), so silent avoids a
          // duplicate. userAuthored:true so the new session counts as genuine engagement and
          // post-processes correctly (a deliberate divergence from the build path, which
          // forwards non-authored markers).
          await this.orchestrator.handleUserMessage(handoff.lastUserMessage, {
            metadata: { source: 'mode_handoff', userAuthored: true, silent: true }
          });
        } else {
          // No authored message to forward (rare — e.g. the switch was triggered off a
          // marker/button turn). Do NOT send '[continue]': Advice has no sentinel handling
          // and it would pollute the transcript. Plain switch + static welcome.
          chatManager.deactivateThinkingIndicator();
          await this.orchestrator.switchMode('ADVICE', transitionData);
        }
        return;
      }

      await this.orchestrator.switchMode(modeName, transitionData);

      // SEAMLESS_TRANSITIONS: Auto-generate opening in new Treatment:Build session.
      // Path B (button click): data.label = "Let's build an experiment" → forward as the
      //   build session's first message, but SILENTLY. Every button group that routes
      //   here echoes (transition-treatment-from-insight sets echo:true), so the click already left a
      //   visible user bubble; rendering this forwarded copy too produced the duplicate
      //   "Let's build an experiment" message. The build prompt's ORIENT arc treats the
      //   first message as a signal regardless of whether it's shown.
      // Path A (LLM direct switchMode): data.label = undefined → hidden [continue] trigger.
      if (modeName === 'TREATMENT' && subMode === 'BUILD') {
        const firstMessage = data.label || '[continue]';
        const isButtonClick = !!data.label;
        await this.orchestrator.handleUserMessage(firstMessage, {
          metadata: {
            source: isButtonClick ? 'button_click' : 'navigation_routing',
            silent: true,
            transitionTrigger: isButtonClick ? 'button_click' : 'llm_direct'
          }
        });
        return;
      }
    });

    // Launch INTAKE session from the screening → intake transition button.
    // Triggered by the "Let's begin" button rendered in _handleIntakeSessionPrereq.
    this.register('start_intake', async (data) => {
      const payload = data.data || {};
      console.log('[ActionRouter] Launching intake session', payload);

      // Snapshot DISCOVERY's current top-row payload before the mode switch
      // so onboarding INTAKE can render the Foundation header (path name +
      // progress bar) while the intake conversation is active. DiscoveryMode
      // wipes its path state on onExit, so this snapshot has to happen here
      // — by the time INTAKE.onEnter runs the data is gone.
      const discoveryMode = this.orchestrator.modes.get('DISCOVERY');
      const inheritedLeftBlock = discoveryMode?.getCurrentLeftBlock?.() || null;
      // Same snapshot-before-teardown reasoning, for the progress popover
      // rather than the header row. Separate payload because the left block is
      // a render contract — see DiscoveryMode.getPathContextSnapshot.
      const inheritedPathContext = discoveryMode?.getPathContextSnapshot?.() || null;

      await this.orchestrator.switchMode('INTAKE', {
        entryType: payload.entryType || 'onboarding',
        returnTo: 'DISCOVERY',
        pathId: payload.pathId || 'foundation',
        resumeSessionId: payload.resumeSessionId || null,
        inheritedLeftBlock,
        inheritedPathContext
      });

      // INTAKE has no user message yet — the agent should speak first.
      // Send a silent trigger so the strategy generates its opening turn.
      // (Same pattern as resume_discovery and Treatment:BUILD seamless transitions.)
      await this.orchestrator.handleUserMessage('[begin]', {
        metadata: {
          source: 'button_click',
          silent: true,
          intakeAction: 'open'
        }
      });
    });

    // Skip the intake conversation entirely. Sibling to start_intake on the
    // intake-launch button group. Streams a brief programmatic transition,
    // then advances the Foundation prerequisite chain via _enterFoundation
    // with initialAction: 'prerequisite_completed' — same handoff pattern
    // used by intake_continue. The orchestrator's prerequisiteIndex moves
    // past intake permanently, so the user is never re-presented with this
    // gate on Foundation re-entry. No mode switch needed — we're already
    // in DISCOVERY (the intake-launch gate renders inside discovery flow).
    this.register('skip_intake', async (data) => {
      const payload = data.data || {};
      const pathId = payload.pathId || 'foundation';
      console.log('[ActionRouter] Skipping intake at foundation gate', payload);

      // Reset any lingering header stage from the prereq gate render.
      this.orchestrator._discoveryMode()?.clearStage();

      // Inline streaming pattern — matches _handleIntakeSessionPrereq
      // (ModeOrchestrator.js:2588-2593), the opener of this same gate.
      // Frontend-only transition: not persisted, not seen by the LLM.
      const chatManager = this.orchestrator.chatManager;
      chatManager.activateThinkingIndicator();
      await new Promise(resolve => setTimeout(resolve, 300));
      await chatManager.streamProgrammaticMessage(SKIP_INTAKE_MESSAGE, {
        chunkDelay: 400,
        messageType: 'system'
      });

      // Advance the orchestrator. Folds the prereq-completion into
      // _enterFoundation's first iteration to avoid a redundant postPathNext
      // call (mirrors intake_continue at ActionRouter.js:243-246).
      const discoveryAPI = new DiscoveryAPI();
      await this.orchestrator._enterFoundation(discoveryAPI, {
        initialAction: 'prerequisite_completed',
        initialActionData: { prerequisiteId: 'intake' }
      });
    });

    // Close INTAKE session — fired when the agent calls the closeIntake action
    // tool. Streams the post-intake handoff body, then renders the
    // paths-forward buttons (Continue Discovery / Start a conversation).
    //
    // Legacy tolerance: pre-retirement closeIntake payloads carried
    // { issueId, concernNumber } from the extraction era — ignored here, so
    // an old rendered button replaying into this handler degrades cleanly.
    this.register('close_intake', async (_data) => {
      console.log('[ActionRouter] Intake closed — rendering paths forward');
      // If this intake produced a captured issue, rewire the "Start a
      // conversation" button into the seamless intake_issue handoff (carrying
      // the source session id + title). A no-concern close leaves no issue, so
      // the button stays a plain advice open. Fetched once here so the button
      // and its handler share the same issue snapshot.
      let issue = null;
      try {
        issue = await API.getActiveIntakeIssue();
      } catch (err) {
        console.warn('[ActionRouter] active-issue lookup failed (falling back to plain advice open):', err);
      }
      const parameterize = issue
        ? (buttons) => buttons.map(b => b.id === 'intake-start-conversation'
          ? {
              ...b,
              action: 'work_on_intake_issue',
              data: { sourceSessionId: issue.sourceSessionId, title: issue.title }
            }
          : b)
        : undefined;
      await this._renderIntakeClosing(POST_INTAKE_BODY, 'intake-complete-onboarding', { parameterize });
    });

    // Carry the concern scoped at intake into a seamless ADVICE session (shared
    // launcher — the same edge the dashboard card uses).
    this.register('work_on_intake_issue', async (data) => {
      const sourceSessionId = data?.data?.sourceSessionId || data?.sourceSessionId;
      const title = data?.data?.title || data?.title || '';
      const { launchIntakeIssueAdvice } = await import('./intakeIssueLauncher.js');
      await launchIntakeIssueAdvice({ sourceSessionId, title });
    });

    // Continue from intake into Discovery — fired when the user clicks the
    // Continue button rendered after closeIntake. Performs the actual mode
    // switch and advances the Foundation prerequisite chain.
    this.register('intake_continue', async (_data) => {
      console.log('[ActionRouter] Continuing from intake into discovery');
      const pathId = 'foundation';
      await this.orchestrator.switchMode('DISCOVERY', { pathId });

      // Route through _enterFoundation so the path metadata gets populated
      // (without it, the header has no path name to render at completion and
      // falls through to the literal "Topic" fallback). prerequisite_completed
      // records the intake finish and returns the next step in one call — fold
      // it into the loop's first iteration to avoid a redundant poll.
      const { DiscoveryAPI } = await import('../core/discoveryApi.js');
      const discoveryAPI = new DiscoveryAPI();
      await this.orchestrator._enterFoundation(discoveryAPI, {
        initialAction: 'prerequisite_completed',
        initialActionData: { prerequisiteId: 'intake' }
      });
    });

    // Return to dashboard
    this.register('return_to_dashboard', async () => {
      this.orchestrator._discoveryMode()?.clearEscalationHistory();
      console.log('[ActionRouter] Returning to dashboard');
      this._sourceSessionId = null;
      const { sessionManager } = await import('../core/SessionManager.js');
      await sessionManager.endSession();
      // Mode/chat/header teardown, then route to / so handleBaseRoute drives
      // the dashboard switch. page() keeps middleware (analytics + panel
      // cleanup) in the loop.
      this.orchestrator.returnToDashboard();
      page('/');
    });

    // Continue the current Treatment:BUILD session after the user declined a
    // proposal and chose to refine it. This is a real visible turn so the normal
    // build REFINE arc can ask what did not fit or draft a revised practice.
    this.register('refine_practice', async () => {
      await this.orchestrator.handleUserMessage('I want to change the experiment.', {
        metadata: { source: 'button_click', practiceRefinementRequested: true }
      });
    });

    // Continue a closed conversation as a fresh, summary-based session_continuation.
    // Reached from the closed-session 404 handoff buttons. Bridges the button
    // click to the proven eventBus 'navigate' + autoPrompt path (the same one the
    // /sessions "Discuss session" button uses). Clears the stale closed chat first.
    this.register('continue_closed_session', async (data) => {
      const sourceSessionId = data.data?.sourceSessionId || null;
      const attemptedMessage = (data.data?.attemptedMessage || '').trim();
      console.log('[ActionRouter] Continuing closed session:', sourceSessionId);

      this._sourceSessionId = null;
      const { sessionManager } = await import('../core/SessionManager.js');
      sessionManager.resetSession();
      this.orchestrator.chatManager?.clearChat?.();

      window.eventBus.emit('navigate', {
        page: 'chat',
        mode: 'ADVICE',
        force: true,
        autoPrompt: {
          text: attemptedMessage || "I'd like to continue our previous conversation.",
          source: 'session_continuation',
          sourceSessionIds: sourceSessionId ? [sourceSessionId] : []
        }
      });
    });

    // Choose a path — navigate to discovery page with discovery tab
    this.register('choose_path', async () => {
      console.log('[ActionRouter] Opening path browser');
      this._sourceSessionId = null;
      this.orchestrator.returnToDashboard();
      // Route via page.js — the discovery route handler runs applyURLParams,
      // which reads ?tab=paths and calls handleTabChange internally.
      page('/discovery?tab=paths');
    });

    // Navigate action - handles page navigation only
    this.register('navigate', async (data) => {
      const path = data.data?.path;

      console.log('[ActionRouter] Navigate action:', { path });

      if (path) {
        const pageName = path.replace(/^\//, '') || 'dashboard';
        window.eventBus.emit('navigate', { page: pageName });
      }
    });

    // navigateTo — the LLM-triggered `navigateTo` tool (app-tools plan Phase 2).
    // Deliberately a NEW handler, not a reuse of `navigate` above: the LLM-action
    // dispatch (`ModeOrchestrator`'s `action:llm-triggered` listener) calls
    // `executeAction(type, { action, input })` with no `data` key, so `navigate`'s
    // `data.data?.path` read would silently no-op here (the bug this handler
    // closes). Reads `data.input.target` instead. A plain `page(route)` call —
    // NOT `returnToDashboard()` — so an in-progress ADVICE session survives the
    // navigation and can be resumed (mirrors how a sidebar nav click behaves
    // mid-chat, not how a session-ending terminus button behaves).
    this.register('navigate_to', async (data) => {
      const target = data?.input?.target;
      const route = NAVIGATE_TO_ROUTES[target];

      if (!route) {
        console.error('[ActionRouter] navigate_to: unknown target', target);
        return;
      }

      // Guest guard (decision #2): the target set itself is unchanged for
      // guests — only arrival at an account-only destination is gated, via
      // the existing conversion-gate machinery (not a bespoke login redirect).
      // `runHandlerOnly('navigate_to', data)` replays this exact call once the
      // account exists, so the guest lands exactly where they asked to go.
      if (window.app?.isGuest === true && NAVIGATE_TO_AUTH_GATED_TARGETS.has(target)) {
        console.log('[ActionRouter] navigate_to: guest hit an account-only destination, opening conversion gate', target);
        await window.app.openConversionGate({
          action: 'navigate_to',
          context: 'default',
          groupId: null,
          actionData: data
        });
        return;
      }

      console.log('[ActionRouter] navigate_to:', { target, route });
      page(route);
    });

    // Invite-your-partner terminus (Foundation completion, unlinked users).
    // Mirrors open_guides: leave the chat first, then route, so the user lands
    // on /partner rather than viewing it behind a live discovery mode.
    this.register('open_partner_invite', async () => {
      console.log('[ActionRouter] Opening partner invite');
      this.orchestrator.returnToDashboard();
      page('/partner');
    });

    // Set-up-partner-sharing terminus (legacy/echo paths — nothing new emits
    // it; the ceremony's invitation beat uses `open_sharing_ceremony`).
    // `?queue=gestalt` is the one-shot entry intent PartnerSection consumes on
    // top of the tab: it opens the consent queue on arrival rather than only
    // scrolling the registry into view, then scrubs the param.
    this.register('open_partner_sharing', async () => {
      console.log('[ActionRouter] Opening partner sharing setup');
      this.orchestrator.returnToDashboard();
      page('/partner?tab=sharing&queue=gestalt');
    });

    // Phase 2's affirmative — BOTH the invitation card and the "Review what
    // {partner} sees" button emit this. Opens the consent queue IN CHAT (the
    // right panel), so the user never leaves the completion they just earned.
    //
    // The orchestrator's deferred phase 3 is KEPT across the launch and mounted
    // when the ceremony RESOLVES. Clearing it on open stranded the user who had
    // just completed a path with no "Continue to Next Path" to click once the
    // panel went away. A navigation teardown deliberately does not resolve (see
    // `SharingCeremonyLauncher._retire`).
    this.register('open_sharing_ceremony', async (data = {}) => {
      // PRECEDENCE: the orchestrator's CONSENT-group id wins over
      // `data.groupId`. `ActionButtons` injects the BUTTON-GROUP id into
      // `actionData` (`ActionButtons.createButton`), so the phase-2 button
      // would otherwise hand the launcher `'sharing-invitation'` — a group
      // that exists in no ledger — in a slot that means consent group. The
      // card emits no groupId at all, which is why this only ever bit the
      // button.
      const groupId = this.orchestrator?._sharingCeremonyGroupId
        || data?.groupId
        || 'gestalt';

      // Armed BEFORE the launch, not inside the resolution: the launcher fires
      // `_notifyResolved(false)` from INSIDE its `right-panel:closed` handler,
      // so a promise armed at that point can only resolve via
      // `waitForPanelClose`'s 500ms safety timeout — the settle choreography
      // would then run against a panel that is already long gone.
      const panelClosed = this.orchestrator?.chatManager?.waitForPanelClose?.() || null;

      const { launchSharingCeremony } = await import('../chat/sharingCeremonyLauncher.js');
      const opened = await launchSharingCeremony({
        groupId,
        // ONE call: `resolveSharingDecision` owns the whole approve/abandon
        // branch, so there is exactly one place the "no forward action" bug
        // could live and exactly one place to test it.
        onResolved: ({ succeeded, bothConfirmed } = {}) => {
          this.orchestrator?.resolveSharingDecision?.({ succeeded, bothConfirmed, panelClosed });
        }
      });
      // Only a real queue locks the card into its dimmed 'open' state — a
      // reason notice (opened === false) leaves it clickable, so the user can
      // try again once the register is ready.
      if (opened) this.orchestrator?.setSharingInvitationCardState?.('open');

      // NOTE: `executeAction` already tore this group's input row down before
      // this handler ran (`_teardownOwnedGroup`), so
      // `_sharingDecisionButtonsHandle.hide()` is redundant on the click paths
      // and load-bearing only on the programmatic ones (teardowns). Do not
      // "simplify" either half into the other.
    });

    // Phase 2's decline — "Not now". Says so (the beat used to dismiss in
    // silence, which is a large part of why it read as a dead end), then mounts
    // phase 3: the options menu and the completion actions. No write, no
    // network, no permanent suppression — the ask re-fires at the next path
    // completion while consent is owed, and `/partner` remains the standing
    // surface the copy points at.
    this.register('dismiss_sharing_invitation', async () => {
      try {
        await this.orchestrator?.chatManager?.streamProgrammaticMessage?.(
          resolveSharingDeclineMessage(),
          { chunkDelay: 400, messageType: 'system' }
        );
      } catch (err) {
        // Cosmetic. Phase 3 below is the forward action and must land either way.
        console.error('[ActionRouter] Sharing decline message failed:', err);
      }
      await this.orchestrator?.mountCompletionPhase?.();
    });

    this.register('open_guides', async (data = {}) => {
      // An optional `guideDefinitionId` deep-links to one guide (the guide
      // announcement card passes it once its guide lands); without it this
      // opens the tab, which is all the completion buttons ever want.
      const guideDefinitionId = data?.guideDefinitionId || null;
      console.log('[ActionRouter] Opening guides tab', guideDefinitionId || '');
      this.orchestrator.returnToDashboard();
      // Route via page.js — the Library route handler runs applyURLParams,
      // which reads ?tab=guides (and ?guide=) and calls handleTabChange internally.
      page(guideDefinitionId
        ? `/library?tab=guides&guide=${encodeURIComponent(guideDefinitionId)}`
        : '/library?tab=guides');
    });

    // Load next topic — user clicked "Continue to Next Topic" after topic completion
    this.register('load_next_topic', async () => {
      console.log('[ActionRouter] Load next topic');
      if (this.orchestrator._handleLoadNextTopic) {
        await this.orchestrator._handleLoadNextTopic();
      }
    });

    this.register('continue_discovery_path', async () => {
      console.log('[ActionRouter] Continue discovery path');
      const orch = this.orchestrator;
      const entryMode = orch._currentDiscoveryEntryMode;
      const entryId = entryMode === 'path'
        ? orch._currentDiscoveryPathId
        : orch._currentDiscoveryScopeId;

      if (!entryId || !orch._runOrchestratorLoop) {
        console.error('[ActionRouter] continue_discovery_path: no entryId available');
        return;
      }

      await orch._runOrchestratorLoop(entryId);
    });

    // Return to discovery home — user clicked "Back to Discovery Home"
    this.register('return_to_discovery_home', async () => {
      console.log('[ActionRouter] Return to discovery home');
      window.eventBus.emit('navigate', { page: 'discovery' });
    });

    // Load next path — always use ranking engine (single authority for "what's next")
    this.register('load_next_path', async (data) => {
      this.orchestrator._discoveryMode()?.clearEscalationHistory();
      console.log('[ActionRouter] Loading next recommended path via ranking engine', data);
      this.orchestrator._currentTargetSubcategory = null;
      await this.orchestrator._runGuidedLoop(new DiscoveryAPI(), { continuation: true });
    });

    // Load a specific path by id — mirrors the path-card click in
    // DiscoverySection.handleWorkOnPath, bypassing the ranking engine.
    // Used by button groups that hardwire a next-step path (e.g. the
    // foundation-complete card pointing at foundation-2).
    this.register('load_path', async (data) => {
      const pathId = data?.data?.pathId;
      if (!pathId) {
        console.error('[ActionRouter] load_path: missing pathId in data.data');
        return;
      }
      console.log('[ActionRouter] Loading specific path', pathId);
      window.eventBus.emit('navigate', {
        page: 'chat',
        mode: 'DISCOVERY',
        pathId
      });
    });

    // Begin / Resume questionnaire — opens the panel (same path as clicking the
    // artifact card). Both labels route through the launcher's _handleClick:
    // ready→open ("Begin") or paused→open ("Resume") via openPanel().
    const openQuestionnairePanel = (label) => {
      const launcher = this.orchestrator._currentQuestionnaireLauncher;
      if (!launcher) {
        console.warn(`[ActionRouter] ${label}: no questionnaire launcher available`);
        return;
      }
      launcher._handleClick();
    };
    this.register('begin_questionnaire', async () => openQuestionnairePanel('begin_questionnaire'));
    this.register('resume_questionnaire', async () => openQuestionnairePanel('resume_questionnaire'));

    // Skip questionnaire — user clicked Skip button (in the chat input)
    this.register('skip_questionnaire', async (data) => {
      console.log('[ActionRouter] Skipping questionnaire');
      const orch = this.orchestrator;
      const scopeId = orch._currentDiscoveryScopeId;
      const sectionCode = orch._currentQuestionnaireSectionCode;

      // 1. Transition launcher to skipped state BEFORE closing panel.
      //    QuestionnaireLauncher._handlePanelClosed checks this.state === 'open',
      //    so setting 'skipped' first prevents the "closed questionnaire" message.
      if (orch._currentQuestionnaireLauncher) {
        orch._currentQuestionnaireLauncher.setState('skipped');
      }

      // Terminal transition — clear the in-input buttons + active-button registry.
      orch._currentQuestionnaireButtonsHandle?.hide?.();
      window.actionButtons?.clearActiveButtons();

      // 2. Close panel if questionnaire was open
      if (orch.currentMode?.questionnaireActive) {
        orch.eventBus.emit('right-panel:close');
        await orch.chatManager.waitForPanelClose();
      }

      // 3. Check localStorage for draft progress
      const questionnaireId = orch._currentSkipQuestionnaireId;
      let hadProgress = false;
      try {
        const draftKey = `questionnaire_draft_${questionnaireId}`;
        const draftJson = localStorage.getItem(draftKey);
        if (draftJson) {
          const draft = JSON.parse(draftJson);
          hadProgress = (draft.responses?.length || 0) > 0;
        }
      } catch (e) { /* ignore */ }

      // 4. Store skip metadata for the next intro's skip signal
      orch._lastSkippedQuestionnaireName = orch._currentSkipQuestionnaireName || null;
      orch._lastSkippedHadProgress = hadProgress;

      // 5. Clear completed name (skip and transition are mutually exclusive)
      orch._lastCompletedQuestionnaireName = null;

      // 6. Build section_declined payload to thread through resolve(). The
      // loop's next iteration fires it via the bundled next endpoint;
      // folding the action into the next poll (rather than firing it directly
      // here) preserves completion-time response fields that a redundant
      // second poll would drop.
      const resumePayload = (scopeId && sectionCode)
        ? { action: 'section_declined', actionData: { sectionCode, hadProgress } }
        : null;

      // 7. Clear DiscoveryMode state
      const mode = orch.currentMode;
      if (mode?.name === 'DISCOVERY') {
        mode.questionnaireActive = false;
        mode.currentQuestionnaireId = null;
      }
      orch._discoveryMode()?.clearStage();

      // 8. Resolve the pending Promise to advance the orchestrator loop
      if (orch._pendingQuestionnaireResolve) {
        orch._pendingQuestionnaireResolve(resumePayload);
      }
    });

    // Discovery intermediary — Go Deeper / Continue to Next
    this.register('discovery_intermediary', async (data) => {
      this.orchestrator._discoveryMode()?.clearEscalationHistory();
      // Terminal transition for this group — clear the in-input buttons + registry so
      // the textarea is restored for the L2 conversation / next step.
      window.app?.modules?.input?.hideButtons?.({ groupId: 'discovery-intermediary' });
      window.actionButtons?.clearActiveButtons();
      const goDeeper = data.action.endsWith(':deeper');
      const scopeId = this.orchestrator._currentDiscoveryScopeId;
      const entryMode = this.orchestrator._currentDiscoveryEntryMode;
      const entryId = entryMode === 'path'
        ? this.orchestrator._currentDiscoveryPathId
        : scopeId;
      if (!entryId) {
        console.error('[ActionRouter] discovery_intermediary: no entryId available');
        return;
      }

      // Stash the declined section name so the next discovery_intro's `decline_bridge`
      // signal lets Haiku compose the honoring sentence into the unified message.
      if (!goDeeper) {
        this.orchestrator._declinedSectionName = this.orchestrator._currentIntermediarySectionName || null;
        this.orchestrator._currentIntermediarySectionName = null;
      }

      // intermediary_decision records the choice and returns the next step in
      // one call — fold it into the loop's first iteration to avoid a
      // redundant poll (which can drop completion-time fields when declining
      // the last section finishes the module).
      if (this.orchestrator._runOrchestratorLoop) {
        await this.orchestrator._runOrchestratorLoop(entryId, {
          initialAction: 'intermediary_decision',
          initialActionData: { goDeeper }
        });
      }
    });

    // End discovery — handles L2 session completion callback if pending.
    //
    // New L2 validation loop: when the callback exists and the reopen cap has
    // not been reached, intercept the close to show the Yes/No validation
    // buttons above the l2-closing-summary card. The callback is NOT fired here —
    // it survives across the validation cycle and only fires on explicit
    // "Yes" confirm (via the `l2_validation` handler) or once the reopen cap
    // is reached on a subsequent endDiscovery.
    //
    // Fallback when extraction returns null: if <l2-closing-summary> cannot be
    // found in the closing text AND no previous takeaways exist from a
    // prior round, there's nothing to validate — fire the callback
    // immediately (same as legacy behavior).
    this.register('end_discovery', async () => {
      console.log('[ActionRouter] Ending discovery session');

      const orch = this.orchestrator;
      if (orch._pendingDiscoveryCallback) {
        const state = orch._l2ValidationState;
        const atCap = state && state.reopenCount >= state.maxReopens;

        if (!atCap && state) {
          // Extract <l2-closing-summary> — fall back to previous round's block if missing
          const extracted = orch._extractL2ClosingSummary(orch._l2ClosingText);
          const takeaways = extracted || state.previousTakeaways;

          if (takeaways) {
            state.previousTakeaways = takeaways;
            state.awaitingValidation = true;
            orch._discoveryMode()?.setL2DotsFinal(true);

            const group = window.buttonGroupRegistry?.get('l2-validation');
            if (group && window.actionButtons) {
              if (group.preMessage) {
                await orch.chatManager.displayAssistantMessage(group.preMessage, {
                  messageType: 'system'
                });
              }
              const buttonGroup = window.actionButtons.createButtonGroup(
                group.buttons,
                { groupId: 'l2-validation' }
              );
              orch.chatManager.messageList.addInteractiveComponent(buttonGroup, {
                type: 'action-buttons',
                data: { groupId: 'l2-validation' }
              });
            }
            // Do NOT null _pendingDiscoveryCallback — it must survive the validation cycle.
            return;
          }
          // No takeaways available → auto-proceed without validation
        }

        // Cap reached, or no takeaways to validate → fire callback as before
        const callback = orch._pendingDiscoveryCallback;
        orch._pendingDiscoveryCallback = null;
        await callback();
        return;
      }

      // Otherwise, close session and return to dashboard
      const { sessionManager } = await import('../core/SessionManager.js');
      await sessionManager.endSession();
      // Mode/chat/header teardown, then route to / so handleBaseRoute drives
      // the dashboard switch (keeps middleware in the loop).
      this.orchestrator.returnToDashboard();
      page('/');
    });

    // L2 validation loop — Yes/No on the endDiscovery takeaways card.
    // - confirm: null the pending callback, fire it (Phase 7 flow runs in callback).
    // - reopen: increment reopenCount + PATCH metadata via _reopenL2Session(),
    //   then display a short programmatic bridge message inviting elaboration.
    //   The Yes/No group is modal in the input; _reopenL2Session hides it, which
    //   restores the textarea so the user's next typed message becomes the first
    //   turn of the clarification round.
    this.register('l2_validation', async (data) => {
      const orch = this.orchestrator;
      const state = orch._l2ValidationState;

      const isConfirm = data.action.endsWith(':confirm');

      if (isConfirm) {
        if (state) state.awaitingValidation = false;
        // Terminal transition — clear the in-input buttons + active-button registry.
        window.app?.modules?.input?.hideButtons?.({ groupId: 'l2-validation' });
        window.actionButtons?.clearActiveButtons();
        const callback = orch._pendingDiscoveryCallback;
        orch._pendingDiscoveryCallback = null;
        if (callback) {
          await callback();
        }
        return;
      }

      // Reopen path: user wants to add or change something.
      await orch._reopenL2Session();

      const variants = [
        "Sure, go ahead and tell me what you'd like to add or clarify.",
        'Of course. What would you like to add or clarify?',
        "Of course. Let's make sure we get this right. What would you like to add or clarify?",
        'No problem. Please share what you would like to add or clarify.',
      ];
      const bridge = variants[Math.floor(Math.random() * variants.length)];

      // Stream the bridge message block-by-block so it fades in like a normal
      // assistant turn. Brief thinking indicator + perceptual delay first so
      // the handoff from "No, discuss further" doesn't feel instant. Same
      // canonical pattern as _renderIntakeClosing and the intake transition
      // (see ModeOrchestrator.js:2195-2200).
      orch.chatManager.activateThinkingIndicator();
      await new Promise(resolve => setTimeout(resolve, 300));
      await orch.chatManager.streamProgrammaticMessage(bridge, {
        chunkDelay: 400,
        messageType: 'system'
      });
    });

    // ============================================================
    // TREATMENT MODE PRACTICE HANDLERS
    // ============================================================

    // Accept a proposed practice draft → flip to 'active'.
    // The standard ActionButtons click handler already adds `action-btn--clicked`
    // to the clicked button (blue) and `action-btn--hiding` to siblings — that's
    // the visual feedback. We then fire a silent synthetic `[practice_accepted]`
    // turn so the agent acknowledges and offers a forward move:
    //   - orientation advice → drives the LANDING (post-accept) turn.
    //   - Treatment:BUILD → drives the build accept-terminus (acknowledgment +
    //     generic conclusion).
    // The two branches are mutually exclusive (_fromOrientationAdvice is only ever
    // true in ADVICE mode). The gate is BUILD-specific on purpose: check-in proposes
    // follow-up practices through the same group and must NOT trigger the build terminus.
    // The `practice-proposal-decision` group echoes the user's choice, so the trigger
    // is silent to avoid a double bubble.
    this.register('accept_practice', async (data) => {
      const practiceId = data.action.split(':')[1];
      if (!practiceId) {
        console.error('[ActionRouter] accept_practice: missing practiceId');
        return;
      }
      // The live card, resolved once: it carries the user's window choice on the
      // way in and gets its chips frozen on the way out. Keyed by the id being
      // accepted, so a staled card or a newer proposal cannot cross-match — and
      // a replayed card carries no data-practice-id, so after a reload this
      // finds nothing and the body is simply omitted.
      const card = document.querySelector(`.structured-card--practice-proposal[data-practice-id="${practiceId}"]`);
      // The explicit range check (not `Number(...) || undefined`) is what keeps
      // the server's 400 unreachable: a 400 does not throw — it lands on the
      // else branch below, which stales a live, untouched draft and dead-ends it.
      const pressed = card?.querySelector('.structured-card__window button[aria-pressed="true"]');
      const pressedDays = Number(pressed?.dataset.days);
      const chosenDays = Number.isInteger(pressedDays) && pressedDays >= 1 && pressedDays <= 90
        ? pressedDays
        : undefined;
      try {
        const result = await API.acceptPractice(practiceId, chosenDays);
        if (result && result.id) {
          // Before the branch cascade so all four accept paths are covered by
          // one call. safeEmit isolates listener exceptions, so a badge bug can
          // never make this successful accept surface the failure toast below.
          emitContentActivated('practice', practiceId);
          // The window has settled on the row; freeze the chips so the card can
          // never assert a value that was never committed. NOT `--stale` — the
          // mutation succeeded, and stale means "replaced".
          card?.querySelectorAll('.structured-card__window button').forEach((chip) => { chip.disabled = true; });

          if (this.orchestrator._fromOrientationAdvice) {
            await this.orchestrator.handleUserMessage('[practice_accepted]', {
              metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceAccepted: true, practiceId }
            });
          } else if (this.orchestrator.currentMode?.name === 'TREATMENT'
                     && this.orchestrator._treatmentSubMode === 'build') {
            await this.orchestrator.handleUserMessage('[practice_accepted]', {
              metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceAccepted: true, practiceId }
            });
          } else if (this.orchestrator._composePracticeActive) {
            // phase-b: inline composePractice accept — the advice conversation continues
            // (no session teardown). The backend gates the post-accept turn on the persisted
            // practiceProposalState + matching id, not on this flag.
            this.orchestrator._composePracticeActive = false;
            await this.orchestrator.handleUserMessage('[practice_accepted]', {
              metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceAccepted: true, practiceId }
            });
          } else if (
            this.orchestrator.currentMode?.name === 'TREATMENT'
            && this.orchestrator.currentMode?.subMode === 'checkin'
            && data.data?.evolveOffer === true
          ) {
            // Check-in evolve offer: only evolveOffer-stamped proposals fire the
            // landing (plain pre-confirm check-in follow-ups stay inert).
            // `currentMode.subMode` is canonical (set by switchMode AND
            // TreatmentMode.onRestore); `_treatmentSubMode` is switchMode-only
            // and null after a session restore. Own try/catch: the accept
            // mutation already succeeded — a marker failure must not surface
            // the outer "Failed to accept experiment" toast.
            try {
              await this.orchestrator.handleUserMessage('[practice_accepted]', {
                metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceAccepted: true, practiceId, evolveOffer: true }
              });
            } catch (markerError) {
              console.error('[ActionRouter] evolve accept landing marker failed:', markerError);
            }
          } else {
            this._handleUnroutedProposalDecision('accept', practiceId);
          }
        } else {
          if (card) card.classList.add('structured-card--practice-proposal--stale');
          window.notificationManager?.showError('This proposal is no longer available');
        }
      } catch (error) {
        console.error('[ActionRouter] Error accepting practice:', error);
        window.notificationManager?.showError('Failed to accept experiment');
      }
    });

    // Reject a proposed practice draft → hard delete. We then fire a silent
    // synthetic marker for the surfaces that need an assistant landing:
    //   - orientation advice → LANDING (post-reject)
    //   - Treatment:BUILD → validates the decline and offers refine/exit paths
    this.register('reject_practice', async (data) => {
      const practiceId = data.action.split(':')[1];
      if (!practiceId) {
        console.error('[ActionRouter] reject_practice: missing practiceId');
        return;
      }
      // The live card, resolved once (mirrors accept_practice): carries the
      // user's window choice on the way in, and gets staled on the way out.
      // Keyed by the id being rejected, so a staled card or a newer proposal
      // cannot cross-match — and a replayed card carries no data-practice-id,
      // so after a reload this finds nothing and the snapshot falls back to
      // the row's own resolved value server-side.
      const card = document.querySelector(`.structured-card--practice-proposal[data-practice-id="${practiceId}"]`);
      const pressed = card?.querySelector('.structured-card__window button[aria-pressed="true"]');
      const pressedDays = Number(pressed?.dataset.days);
      const chosenDays = Number.isInteger(pressedDays) && pressedDays >= 1 && pressedDays <= 90
        ? pressedDays
        : undefined;
      let result;
      let alreadyGoneFromThrow = false;
      try {
        result = await API.deletePractice(practiceId, { draftOnly: true, checkInIntervalDays: chosenDays });
      } catch (error) {
        alreadyGoneFromThrow = error?.message?.includes('404');
        if (!alreadyGoneFromThrow) {
          console.error('[ActionRouter] Error rejecting practice:', error);
          window.notificationManager?.showError('Failed to reject experiment');
          return;
        }
      }
      const deleteSucceeded = result?.deleted === true || result?.success === true;
      const alreadyGone = alreadyGoneFromThrow || result?.code === 'PRACTICE_NOT_FOUND' || result?.status === 404;
      if (result?.code === 'PRACTICE_ALREADY_DECIDED') {
        const staleCard = document.querySelector(`.structured-card--practice-proposal[data-practice-id="${practiceId}"]`);
        if (staleCard) staleCard.classList.add('structured-card--practice-proposal--stale');
        document.querySelector('.action-buttons[data-group-id="practice-proposal-decision"]')?.remove();
        this.orchestrator._composePracticeActive = false;
        return;
      }
      if (!deleteSucceeded && !alreadyGone) {
        console.error('[ActionRouter] reject_practice: delete request failed', result);
        window.notificationManager?.showError('Failed to reject experiment');
        return;
      }

      if (card) card.classList.add('structured-card--practice-proposal--declined');
      if (this.orchestrator._fromOrientationAdvice) {
        await this.orchestrator.handleUserMessage('[practice_rejected]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceRejected: true, practiceId }
        });
      } else if (this.orchestrator.currentMode?.name === 'TREATMENT'
                 && this.orchestrator._treatmentSubMode === 'build') {
        await this.orchestrator.handleUserMessage('[practice_rejected]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceRejected: true, practiceId }
        });
      } else if (this.orchestrator._composePracticeActive) {
        // phase-b: inline composePractice reject — a light continued advice turn; the user's
        // "change X" then drives a composePractice re-call (the refinement loop).
        this.orchestrator._composePracticeActive = false;
        await this.orchestrator.handleUserMessage('[practice_rejected]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceRejected: true, practiceId }
        });
      } else if (
        this.orchestrator.currentMode?.name === 'TREATMENT'
        && this.orchestrator.currentMode?.subMode === 'checkin'
        && data.data?.evolveOffer === true
      ) {
        // Check-in evolve offer reject landing — same gate + error separation as
        // the accept branch (the delete already succeeded; a marker failure must
        // not toast "Failed to reject experiment").
        try {
          await this.orchestrator.handleUserMessage('[practice_rejected]', {
            metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceRejected: true, practiceId, evolveOffer: true }
          });
        } catch (markerError) {
          console.error('[ActionRouter] evolve reject landing marker failed:', markerError);
        }
      } else {
        this._handleUnroutedProposalDecision('reject', practiceId);
      }
    });

    // CUTOVER COMPAT: `continue_practice` and `set_aside_practice` are the
    // PREVIOUS labels of `practice-continue-decision`, replaced by
    // `close_practice` / `keep_open_practice` below. They stay registered only
    // so buttons already rendered into in-flight transcripts still resolve;
    // nothing emits them any more. Retire both in a later release.
    //
    // Keep going with a practice from the check-in CONTINUE terminus. Unlike
    // its `set_aside_practice` sibling this performs NO mutation — the practice
    // is already `active` and staying that way is the whole point of the click,
    // so the marker is the entire job. The agent acknowledges the choice and
    // offers `treatment-build-conclusion`, letting the user leave when they
    // want to rather than closing the session out from under them.
    this.register('continue_practice', async (data) => {
      const practiceId = data.action.split(':')[1] || null;

      // Fire-and-report: there is no preceding mutation to contradict, so a
      // marker failure is the whole failure and the user should see it — they
      // clicked something and nothing happened. This differs from
      // `set_aside_practice`, which must stay quiet because its archive already
      // committed.
      try {
        await this.orchestrator.handleUserMessage('[practice_continued]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceId }
        });
      } catch (markerError) {
        console.error('[ActionRouter] continue landing marker failed:', markerError);
        window.notificationManager?.showError('Could not send that — please try again');
      }
    });

    // Set a practice aside from the check-in CONTINUE terminus. Archive is
    // reversible (`/practices/:id/restore`), so this is a low-stakes pause, not
    // a deletion — the register in the landing turn reflects that.
    this.register('set_aside_practice', async (data) => {
      const practiceId = data.action.split(':')[1];
      if (!practiceId) {
        console.error('[ActionRouter] set_aside_practice: missing practiceId');
        window.notificationManager?.showError('Could not archive this experiment');
        return;
      }

      try {
        await API.archivePractice(practiceId);
      } catch (error) {
        // The practice moved on in another tab (completed elsewhere, or already
        // archived) → 409 PRACTICE_NOT_ARCHIVABLE. Benign stale state: fall
        // through to the landing rather than toasting a failure, mirroring how
        // reject_practice treats PRACTICE_ALREADY_DECIDED.
        const staleState = error?.message?.includes('409')
          || error?.message?.includes('PRACTICE_NOT_ARCHIVABLE');
        if (!staleState) {
          console.error('[ActionRouter] Error setting practice aside:', error);
          window.notificationManager?.showError('Could not archive this experiment');
          return;
        }
        console.log('[ActionRouter] set_aside_practice: practice already inactive, treating as stale');
      }

      // Own try/catch: the archive already committed — a marker failure must
      // not surface an error implying the practice is still active.
      try {
        await this.orchestrator.handleUserMessage('[practice_set_aside]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceId }
        });
      } catch (markerError) {
        console.error('[ActionRouter] set-aside landing marker failed:', markerError);
      }
    });

    // Close an experiment into a finding — the recommended branch of the report
    // conversation's `practice-continue-decision`. Like `continue_practice` (and
    // unlike `keep_open_practice`) this performs NO mutation: the finding does
    // not exist yet, so the marker hands the turn back to the model to author
    // the takeaway and call `completePractice`. The user then confirms on
    // `practice-completion-decision` as usual, and THAT confirm is what closes
    // the row.
    this.register('close_practice', async (data) => {
      const practiceId = data.action.split(':')[1] || null;

      // Fire-and-report: no preceding mutation to contradict, so a marker
      // failure is the whole failure and the user must see it — they clicked
      // something and nothing happened.
      try {
        await this.orchestrator.handleUserMessage('[practice_close_requested]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceId }
        });
      } catch (markerError) {
        console.error('[ActionRouter] close-requested landing marker failed:', markerError);
        window.notificationManager?.showError('Could not send that — please try again');
      }
    });

    // Give an experiment another observation window — the second branch of
    // `practice-continue-decision`. Follows `set_aside_practice`'s shape, not
    // `continue_practice`'s: mutate first, then land. The mutation is what moves
    // `windowClosesAt`, and its `sessionId` body is load-bearing — the route
    // stamps `metadata.outcome = 'kept_open'` from it, which is what makes a
    // brief report ("Didn't happen.") still produce a PracticeLog.
    this.register('keep_open_practice', async (data) => {
      const practiceId = data.action.split(':')[1];
      if (!practiceId) {
        console.error('[ActionRouter] keep_open_practice: missing practiceId');
        window.notificationManager?.showError('Could not keep this experiment open');
        return;
      }

      try {
        await API.keepPracticeOpen(practiceId, this.orchestrator._getSessionId());
      } catch (error) {
        // The practice moved on in another tab (completed elsewhere, or the
        // lapse sweep archived it) → 409 PRACTICE_NOT_EXTENDABLE. Benign stale
        // state: fall through to the landing rather than toasting a failure,
        // mirroring `set_aside_practice`.
        const staleState = error?.message?.includes('409')
          || error?.message?.includes('PRACTICE_NOT_EXTENDABLE');
        if (!staleState) {
          console.error('[ActionRouter] Error keeping practice open:', error);
          window.notificationManager?.showError('Could not keep this experiment open');
          return;
        }
        console.log('[ActionRouter] keep_open_practice: practice no longer extendable, treating as stale');
      }

      // Own try/catch: the window extension already committed — a marker
      // failure must not surface an error implying it did not.
      try {
        await this.orchestrator.handleUserMessage('[practice_continued]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, skipCounting: true, practiceId }
        });
      } catch (markerError) {
        console.error('[ActionRouter] keep-open landing marker failed:', markerError);
      }
    });

    // ─── Partner Memo (coupled ADVICE) ──────────────────────────────────────
    // The memo review buttons (`partner-memo-review`) ride the same staged-message
    // machinery as the practice flow: a click fires a client-provided auto user
    // message + metadata flag; the live agent (re-)calls composeMemo; the memo card
    // streams into the same assistant message via appendContent (§7).
    //
    // Under auto tool_choice the agent is NOT guaranteed to stream a preamble before
    // the tool call, so the generate/refine handlers activate the thinking indicator
    // IMMEDIATELY on click to cover the Layer-2 latency (W2/EC3) — the UX must not
    // depend on an agent preamble. handleUserMessage re-activates it too; the eager
    // call just closes the gap before the turn's own activation.

    // generate_partner_memo — dispatched by the (deferred) agent pill or the dev
    // trigger (§12.A). Wires the *click*, not the pill display.
    //
    // The self-sent text branches on the owning group id. Inside a memo_compose
    // session the standing "I'm ready, write the note" pill and the agent-posed
    // "Draft the note" offer are READINESS signals: the conversation is the
    // material and the user is choosing the moment. "I think I'm ready…" is
    // load-bearing — it states readiness without claiming completeness, so the
    // backend's `<memo_compose_depth>` clause keeps its one-more-question path
    // as the default and reserves the compose-now escape hatch for a user who
    // actually types "just write it". The Create menu item and the post-takeaway
    // cascade offers are fresh requests in an ordinary Advice session, not
    // readiness signals, so they keep the "I'd like to write a note…" wording.
    // Metadata is identical on both branches — the backend keys on the flag,
    // not the text.
    this.register('generate_partner_memo', async (actionData) => {
      // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
      // Real pre-turn latency: getLinkedPartnerName() is a network fetch, then
      // handleUserMessage → SSE. handleUserMessage re-arms the same phase (a
      // no-op re-entry); executeAction's catch tears down on failure (§4.1).
      this.orchestrator.chatManager?.activateThinkingIndicator?.(undefined, { waiting: true });
      const partnerName = await getLinkedPartnerName();
      const isReadinessSignal = MEMO_COMPOSE_READINESS_GROUP_IDS.has(actionData?.groupId);
      let message;
      if (isReadinessSignal) {
        message = partnerName
          ? `I think I'm ready to write the note to ${partnerName}.`
          : "I think I'm ready to write the note to my partner.";
      } else {
        message = partnerName
          ? `I'd like to write a note to ${partnerName}.`
          : "I'd like to write a note to my partner.";
      }
      await this.orchestrator.handleUserMessage(message, {
        metadata: { source: 'button_click', generatePartnerMemoRequested: true }
      });
    });

    // memo_entry-only reply pill. The metadata flag is intentionally inert and
    // retained for telemetry-shape parity with generatePartnerMemoRequested;
    // the agent acts on the visible message text, not this flag.
    this.register('generate_partner_memo_reply', async () => {
      // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
      // Same shape as generate_partner_memo above: fetch latency, then SSE.
      this.orchestrator.chatManager?.activateThinkingIndicator?.(undefined, { waiting: true });
      const partnerName = await getLinkedPartnerName();
      const message = partnerName
        ? `I'd like to write back to ${partnerName} about this.`
        : "I'd like to write back to my partner about this.";
      await this.orchestrator.handleUserMessage(message, {
        metadata: { source: 'button_click', generatePartnerMemoReplyRequested: true }
      });
    });

    // compose_practice — the repointed build affordances (the cascade
    // advice-offer-practice "Build an experiment" button and the server-injected
    // build-a-practice pill; the transition-treatment-build / session-conclusion
    // groups were retired by the terminus redesign) fire this instead of the
    // retired switch_mode:TREATMENT:BUILD. It sends a natural user
    // message so the advice agent calls composePractice inline (no mode switch, no session
    // teardown). Mid-stream clicks (C1) are handled UPSTREAM by PillDispatch — the
    // queue-until-idle contract for ALL pill-row clicks (this handler's private copy was
    // generalized there; see pill-streaming-interaction-design.md §7.1). The input-mounted
    // trigger groups are modal/idle-by-construction; a residual SESSION_BUSY race degrades
    // to the executeAction failure path (pill restored via _buildAgentPill).
    this.register('compose_practice', async () => {
      this.orchestrator.chatManager?.activateThinkingIndicator?.();
      await this.orchestrator.handleUserMessage("Let's turn this into an experiment I can try.", {
        metadata: { source: 'button_click', composePracticeRequested: true }
      });
    });

    // request_takeaways — the "Sum this up" wrap-up pill (advice-wrap-up), the
    // default ending for both standard-Advice voices from response 4 on. Sends a
    // plain visible request, which is already sufficient permission for the
    // emitAdviceTakeaway tool ("when the user explicitly asks"); no directive and
    // no durable intent, so it is deliberately absent from
    // CONTRACT_BEARING_ADVICE_ACTIONS and from ADVICE_BUTTON_CONTRACT.
    //
    // GUARD THE FIRE, not just the render: PillRow.removePill never clears the
    // PillDispatch queue, so a click queued during a turn in which the model lands
    // SPONTANEOUSLY still fires at chat:idle — and executeAction's eager teardown
    // would take down the just-mounted post-takeaway group before self-sending a
    // request the takeaway tool can no longer serve. When the card is already in
    // the transcript there is nothing to ask for: drop the queue and no-op.
    this.register('request_takeaways', async () => {
      if (hasRenderedAdviceTakeaway()) {
        window.app?.modules?.pillDispatch?.clear?.();
        console.log('[ActionRouter] request_takeaways: takeaway already landed — no-op');
        return;
      }
      this.orchestrator.chatManager?.activateThinkingIndicator?.();
      await this.orchestrator.handleUserMessage("Let's sum up where we landed.", {
        metadata: { source: 'button_click', takeawayRequested: true }
      });
    });

    // cascade_decline:<action> — the "Not now" button on an advice-cascade offer
    // group (advice-offer-practice / advice-offer-memo). Plain self-sending
    // handler (the offer groups are INPUT-mounted — modal, torn down per-turn —
    // so no PillDispatch queue is needed; a residual race degrades to the
    // SESSION_BUSY backstop). Fires the silent wake marker carrying the declined
    // action id; the backend appends it to metadata.cascadeDeclined and advances
    // the cascade on the same turn.
    this.register('cascade_decline', async (data) => {
      const action = data.action.split(':')[1];
      if (!action) {
        console.error('[ActionRouter] cascade_decline: missing action id');
        return;
      }
      await this.orchestrator.handleUserMessage(`[cascade_declined:${action}]`, {
        metadata: {
          source: 'button_click',
          silent: true,
          systemTrigger: true,
          skipCounting: true,
          cascadeDeclined: action
        }
      });
    });

    // ─── Terminus recommendation flow ───────────────────────────────────────
    // Three clicks, one shape: each fires a silent wake marker whose metadata
    // tells the backend which beat to voice. The clicked LABEL is the visible
    // user turn (the catalog flags these buttons `echo: true`); the marker itself
    // is `silent` so it never renders a second bubble, and `systemTrigger` hides
    // it on replay. `skipCounting` keeps the wake out of the message quota.

    // request_recommendation — the post-takeaway teaser's "What do you
    // recommend?". Pulls the analyzer-informed recommendation beat.
    this.register('request_recommendation', async () => {
      await this.orchestrator.handleUserMessage('[recommendation_requested]', {
        metadata: {
          source: 'button_click',
          silent: true,
          systemTrigger: true,
          skipCounting: true,
          recommendationRequested: true
        }
      });
    });

    // request_deep_mode — the teaser's "Keep going deeper", the Create menu's
    // "Deep exploration" item, and the recommendation turn's `none_scored`
    // branch all resolve to this same silent wake. Verbatim shape of
    // `request_recommendation` above, with `deepModeRequested: true` in place
    // of `recommendationRequested: true` — the backend derives `viaSurface`
    // itself from the interaction row's catalog group id, never from a
    // client-supplied field.
    this.register('request_deep_mode', async () => {
      await this.orchestrator.handleUserMessage('[deep_mode_requested]', {
        metadata: {
          source: 'button_click',
          silent: true,
          systemTrigger: true,
          skipCounting: true,
          deepModeRequested: true
        }
      });
    });

    // recommendation_decline — "Not now" on the memo-lead recommendation group.
    // Distinct from `cascadeDeclined` (which names a cascade action id): this
    // declines the recommendation itself, and the rearm projection falls through
    // to the classic post-takeaway group from there.
    this.register('recommendation_decline', async () => {
      await this.orchestrator.handleUserMessage('[recommendation_declined]', {
        metadata: {
          source: 'button_click',
          silent: true,
          systemTrigger: true,
          skipCounting: true,
          recommendationDeclined: true
        }
      });
    });

    // choose_direction:<index> — one of the pre-selected experiment directions.
    // Registered ONCE: `executeAction` dispatches on the prefix before the ':'
    // (same as `cascade_decline:<action>` / `accept_practice:<id>`).
    //
    // The click is contract-bearing (CONTRACT_BEARING_ADVICE_ACTIONS), so by the
    // time this handler runs the server has already validated the slot against
    // the persisted candidates and written `selectedPracticeCode`. A stale pick
    // never reaches here — it aborts on the 409 above. The index is re-parsed only
    // as a guard: a malformed action must not wake a turn with no direction behind
    // it. The index itself is NOT forwarded — the server resolved it already.
    this.register('choose_direction', async (data) => {
      const index = Number(String(data?.action || '').split(':')[1]);
      if (!Number.isInteger(index) || index < 0) {
        console.error('[ActionRouter] choose_direction: missing or invalid index', data?.action);
        return;
      }
      await this.orchestrator.handleUserMessage('[path_selected]', {
        metadata: {
          source: 'button_click',
          silent: true,
          systemTrigger: true,
          skipCounting: true,
          pathSelected: true
        }
      });
    });

    // memo_refine_warmer / memo_refine_shorter — the user's "Make it warmer" bubble
    // shows immediately (handleUserMessage); the agent re-calls composeMemo with a
    // directive; the revised card streams into that same assistant message and the
    // one-draft invariant replaces the old draft (prior card marked stale on render).
    const registerMemoRefine = (action, tilt, message) => {
      this.register(action, async (data) => {
        const memoId = data.action.split(':')[1] || data?.input?.memoId || null;
        this.orchestrator.chatManager?.activateThinkingIndicator?.();
        await this.orchestrator.handleUserMessage(message, {
          metadata: { source: 'button_click', memoRefinementRequested: true, tilt, memoId }
        });
      });
    };
    registerMemoRefine('memo_refine_warmer', 'warmer', 'Make it warmer');
    registerMemoRefine('memo_refine_shorter', 'shorter', 'Make it shorter');

    // memo_send:<memoId> — send the drafted note. A 409 means the draft is gone
    // (cross-session one-draft delete, or the couple changed) → render the card
    // stale + "no longer available". The echo bubble ("Send note") is the in-line
    // confirmation; a success toast reinforces it.
    this.register('memo_send', async (data) => {
      const memoId = data.action.split(':')[1];
      if (!memoId) {
        console.error('[ActionRouter] memo_send: missing memoId');
        return;
      }
      try {
        await new DiscoveryAPI().sendMemo(memoId);
        window.notificationManager?.showSuccess?.('Note sent');
        // Post-memo wake (Phase C, WI-3a; terminus D3/D8): fired INSIDE the
        // try immediately after sendMemo resolves — the generic catch below does
        // NOT early-return, so a post-block fire would also fire on error paths
        // (false completion). Normal ADVICE sessions advance the cascade; a
        // memo_entry session receives a cascade-inert acknowledgment instead.
        await this._fireMemoDoneWake('sent');
      } catch (err) {
        if (err?.code === 'MEMO_REPLY_EXISTS') {
          // One live reply per root (reply-pair linkage): a concurrent send already
          // took the slot. Distinct message from the generic "no longer available".
          const card = document.querySelector(`.structured-card--memo-proposal[data-memo-id="${memoId}"]`);
          if (card) card.classList.add('structured-card--memo-proposal--stale');
          window.notificationManager?.showError?.("You've already replied to this note.");
        } else if (err?.status === 409 || err?.code === 'MEMO_NOT_AVAILABLE') {
          const card = document.querySelector(`.structured-card--memo-proposal[data-memo-id="${memoId}"]`);
          if (card) card.classList.add('structured-card--memo-proposal--stale');
          window.notificationManager?.showError?.('This note is no longer available.');
        } else {
          console.error('[ActionRouter] Error sending memo:', err);
          window.notificationManager?.showError?.('Failed to send note');
        }
      }
    });

    // memo_discard:<memoId> — hard-delete the draft. A 409 means the status is
    // no longer draft, but cannot prove whether it was discarded or sent.
    this.register('memo_discard', async (data) => {
      const memoId = data.action.split(':')[1];
      if (!memoId) {
        console.error('[ActionRouter] memo_discard: missing memoId');
        return;
      }
      let discarded = false;
      try {
        await new DiscoveryAPI().discardMemo(memoId);
        discarded = true;
      } catch (err) {
        if (!(err?.status === 409 || err?.code === 'MEMO_NOT_AVAILABLE')) {
          console.error('[ActionRouter] Error discarding memo:', err);
          window.notificationManager?.showError?.('Failed to discard note');
          return;
        }
        // A concurrent operation may have discarded OR sent the draft. Mark the
        // card stale below, but do not claim an outcome we cannot confirm.
      }
      const card = document.querySelector(`.structured-card--memo-proposal[data-memo-id="${memoId}"]`);
      if (card) {
        card.classList.add(discarded
          ? 'structured-card--memo-proposal--discarded'
          : 'structured-card--memo-proposal--stale');
      }
      if (discarded) {
        await this._fireMemoDoneWake('discarded');
      }
    });

    this.register('confirm_practice_completion', async (data) => {
      const practiceId = data.action.split(':')[1];
      const sessionId = this.orchestrator._getSessionId();
      if (!practiceId || !sessionId) {
        console.error('[ActionRouter] confirm_practice_completion: missing practiceId or sessionId');
        return;
      }
      try {
        const result = await API.practiceCompletionDecision(practiceId, sessionId, 'confirm');
        if (result?.ok) {
          await this.orchestrator.handleUserMessage('[practice_completion_confirmed]', {
            metadata: { source: 'button_click', silent: true, systemTrigger: true }
          });
        } else {
          window.notificationManager?.showError('Failed to confirm experiment completion');
        }
      } catch (error) {
        console.error('[ActionRouter] Error confirming practice completion:', error);
        window.notificationManager?.showError('Failed to confirm experiment completion');
      }
    });

    this.register('decline_practice_completion', async (data) => {
      const practiceId = data.action.split(':')[1];
      const sessionId = this.orchestrator._getSessionId();
      if (!practiceId || !sessionId) {
        console.error('[ActionRouter] decline_practice_completion: missing practiceId or sessionId');
        return;
      }
      try {
        const result = await API.practiceCompletionDecision(practiceId, sessionId, 'decline');
        if (result?.ok) {
          await this.orchestrator.handleUserMessage('[practice_completion_declined]', {
            metadata: { source: 'button_click', silent: true, systemTrigger: true }
          });
        } else {
          window.notificationManager?.showError('Failed to record your decision');
        }
      } catch (error) {
        console.error('[ActionRouter] Error declining practice completion:', error);
        window.notificationManager?.showError('Failed to record your decision');
      }
    });

    // Show buttons — display a predefined button group (via show_buttons tool call)
    this.register('show_buttons', async (data) => {
      const groupId = data?.input?.groupId || data.action.split(':')[1] || data.value;
      if (!groupId) {
        console.warn('[ActionRouter] show_buttons: no groupId');
        return;
      }

      // Remember the input this group was mounted with so a re-arm can restore
      // it (see `_lastGroupInput` in the constructor). Re-arms come back through
      // this same handler, so the memo tracks the live render either way.
      this._lastGroupInput.set(groupId, data?.input || {});

      // Explicit server-built button array: render it directly instead of the
      // catalog. MUST run before the registry lookup so a shell entry with an
      // empty buttons array doesn't win. Render options (input mounting, text
      // entry) still resolve from the shell via groupId; explicit values here
      // are the registry-miss fallback.
      const explicitButtons = Array.isArray(data?.input?.buttons) && data.input.buttons.length > 0
        ? data.input.buttons
        : null;
      if (explicitButtons) {
        if (window.actionButtons) {
          const buttonGroup = window.actionButtons.createButtonGroup(explicitButtons, { groupId });
          this.orchestrator.chatManager.messageList.addInteractiveComponent(buttonGroup, {
            type: 'action-buttons',
            data: { groupId, renderLocation: 'input', allowTextEntry: true }
          });
        }
        return;
      }

      const group = window.buttonGroupRegistry?.get(groupId);
      if (!group) {
        console.warn('[ActionRouter] Unknown button group:', groupId);
        return;
      }

      // Passive persistent pill (chat-input-redesign Phase 3): a `pill-row` group is
      // registered as a Tier-2 pill in the floating PillRow and MUST return BEFORE
      // createButtonGroup — it must not mount to the transcript/input, and must not
      // pollute `activeButtons` (which is sent as button-context with the NEXT user
      // message). Idempotent: re-firing via a pendingAction reload dedups by pill id.
      //
      // `setAgentPills` replaces the whole set, so surfacing a new pill-row group
      // live must carry the ALREADY-LIVE pill groups too. Advice terminus pills are
      // a SINGLE-pill contract (advice-unified-terminus §3.9): only one is ever
      // valid at a time, so when the incoming group is an Advice terminus pill we
      // REPLACE (drop any other terminus pill from the carried set) rather than
      // union. Non-terminus pill-row tenants keep the additive union.
      if (group.renderLocation === 'pill-row') {
        const incomingIsTerminus = ADVICE_TERMINUS_PILL_GROUP_IDS.includes(groupId);
        const liveGroupIds = (window.app?.modules?.pillRow?.agentPills || [])
          .map((pill) => pill.groupId)
          .filter(Boolean)
          .filter((id) => !(incomingIsTerminus && ADVICE_TERMINUS_PILL_GROUP_IDS.includes(id)));
        this.syncPillRowFromGroups([...new Set([...liveGroupIds, groupId])]);
        return;
      }

      // One-off: closing message + divider before orientation advice conclusion
      // (both default and post-accept variants share the same wrap-up).
      if (
        groupId === 'orientation-session-conclusion'
        || groupId === 'orientation-session-conclusion-accepted'
      ) {
        await this.orchestrator.chatManager.displayAssistantMessage(
          "Let's end the session on that note. We can continue this discussion anytime you like.",
          { messageType: 'system' }
        );
        this.orchestrator.messageList.addDividerMessage('Exiting Session', { scroll: false });
      }

      // Inject deterministic pre-message if defined on the button group
      if (group.preMessage) {
        await this.orchestrator.chatManager.displayAssistantMessage(group.preMessage, {
          messageType: 'system'
        });
      }

      // Parameterize practice proposal buttons with practiceId so the
      // accept/reject/set-aside handlers can recover the practice ID from the
      // action string. `practice-continue-decision` belongs here for the same
      // reason — both its buttons (`close_practice`, `keep_open_practice`) are
      // practice-scoped.
      let buttons = group.buttons;
      if (
        (groupId === 'practice-proposal-decision'
          || groupId === 'practice-completion-decision'
          || groupId === 'practice-continue-decision')
        && data?.input?.practiceId
      ) {
        buttons = group.buttons.map(b => ({ ...b, action: `${b.action}:${data.input.practiceId}` }));
      }

      // Check-in evolve offer: the backend stamps
      // `evolveOffer: true` onto the proposal decision buttons ONLY when the
      // proposal is the post-completion evolution offer. Inject it into each
      // button's data payload (carried via dataset.actionData → click event
      // data.data) so the accept/reject handlers can gate the landing markers.
      // Keyed STRICTLY on the input field — plain proposal renders must stay
      // inert (provenance rests on the backend threading this only for an
      // active post-completion evolution offer).
      if (groupId === 'practice-proposal-decision' && data?.input?.evolveOffer === true) {
        buttons = buttons.map(b => ({ ...b, data: { ...(b.data || {}), evolveOffer: true } }));
      }

      // phase-b N1: a `practice-proposal-decision` rendered in normal ADVICE (mode ADVICE, not the
      // orientation flow, not TREATMENT) is a composePractice proposal. Flag it AT RENDER (the tool
      // call never traverses switchMode) so accept/reject fires the inline advice continuation.
      // Recomputed fresh each render so it can't go stale across proposal types (orientation keeps
      // _fromOrientationAdvice; TREATMENT keeps its sub-mode gate — both mutually exclusive with this).
      if (groupId === 'practice-proposal-decision') {
        this.orchestrator._composePracticeActive =
          this.orchestrator.currentMode?.name === 'ADVICE'
          && !this.orchestrator._fromOrientationAdvice;
      }

      // Parameterize partner-memo-review buttons with memoId so the send/discard
      // handlers can recover the id from the action string (e.g. memo_send:<id>).
      // The refine buttons are stamped too but recover the id via data.input in
      // their handlers; stamping keeps every action in the group id-scoped.
      if (groupId === 'partner-memo-review' && data?.input?.memoId) {
        buttons = group.buttons.map(b => ({ ...b, action: `${b.action}:${data.input.memoId}` }));
      }

      // Experiment-direction slots (terminus recommendation flow): the catalog
      // carries three static slots labelled "Option 1/2/3". Both the live
      // candidate count and the per-slot labels ride the mount input
      // (`terminateWith.input.{count,labels}`) — the model supplies neither, and
      // a re-present is intercepted server-side and re-injected from the
      // persisted candidates. `labels[i]` is the resolved label for slot i (the
      // model-authored direction title, already normalized and length-checked
      // by the server, which is the sole length authority) and matches the
      // directions card's `<item><header>` row at the same position 1:1.
      //
      // FAIL SAFE (count): a missing, zero, non-integer or out-of-range count
      // renders ALL THREE slots rather than nothing — an over-wide slot row is
      // recoverable (the server's 409 gate refuses an index with no candidate
      // behind it), an empty one is a dead end.
      //
      // FAIL SAFE (labels): a non-array `labels`, or a non-string/blank entry,
      // leaves that slot on its catalog label. Never render an empty chip. No
      // client-side length check — clamping here would only produce a truncated
      // chip the server already decided against.
      if (groupId === 'advice-experiment-directions') {
        const count = data?.input?.count;
        if (Number.isInteger(count) && count >= 1 && count < buttons.length) {
          buttons = buttons.slice(0, count);
        }
        const labels = data?.input?.labels;
        if (Array.isArray(labels)) {
          buttons = buttons.map((b, i) => {
            const t = labels[i];
            return (typeof t === 'string' && t.trim()) ? { ...b, label: t.trim() } : b;
          });
        }
      }

      // Mounting an input group in ADVICE means the terminus/cascade has begun
      // (post-takeaway, complement offer, proposal decision, or conclusion), so
      // the early pill row must not linger beside it (advice-unified-terminus
      // §3.9). Clear every live Advice terminus pill on input-group mount.
      // Idempotent/no-op when the row is already empty.
      if (this.orchestrator.currentMode?.name === 'ADVICE') {
        clearAllAdviceTerminusPills();
      }

      if (window.actionButtons) {
        // Pass { groupId } so container.dataset.groupId is set — required for
        // the proposal-card stale-walk to find prior button containers by group.
        const buttonGroup = window.actionButtons.createButtonGroup(buttons, { groupId });
        this.orchestrator.chatManager.messageList.addInteractiveComponent(buttonGroup, {
          type: 'action-buttons',
          data: { groupId }
        });
      }
    });

    // Show questionnaire — re-renders the questionnaire artifact via Sonnet tool call
    this.register('show_questionnaire', async () => {
      const orch = this.orchestrator;
      const launcher = orch._currentQuestionnaireLauncher;
      if (!launcher) {
        console.warn('[ActionRouter] show_questionnaire: no launcher available');
        return;
      }

      // QuestionnaireLauncher.render() creates a new Artifact each time — safe to call again
      const element = launcher.render();
      orch.chatManager.messageList.addInteractiveComponent(element, {
        type: 'questionnaire-launcher',
        data: { questionnaireId: launcher.questionnaireId }
      });

      // Re-show the input action buttons (Begin/Resume + Skip|Move on), matching the
      // launcher's current state.
      orch._showQuestionnaireInputButtons({
        resumed: launcher.state === 'paused',
        skippable: orch._currentQuestionnaireSkippable
      });
    });

    // Advice stance bridge — "Let's get practical" (Explore → Act)
    // Reused in two contexts:
    //   - ATTUNEMENT: agent detected action signal in opener; clicking
    //     completes attunement AND flips stance.
    //   - End of Explore arc (PATH_WORK): clicking just flips stance.
    // The handler always sends `completeAttunement: true` — the backend
    // route ignores the flag if attunement is already complete, so this is
    // safe in both contexts and keeps the frontend logic simple.
    this.register('set_stance', async (data) => {
      const stance = data.action.split(':')[1]; // 'advisor' | 'explore' | 'act' | 'support'
      const sessionId = this.orchestrator._getSessionId();
      // Voice-fork / supportive-landing-fork buttons carry data.routingFork.
      // (button group `data` field → ActionButtons emits it as actionData.data)
      const routingFork = data?.data?.routingFork === true;
      // Voice-correction pills (advice-unified-terminus) carry data.voiceCorrection.
      const voiceCorrection = data?.data?.voiceCorrection === true;

      // ── Voice-correction path (§3.9) ──────────────────────────────────────
      // Success-gated: persist the stance (with completeAttunement:false →
      // user_explicit) FIRST; only on success render the visible request and send
      // the silent continuation. The correction pill was already removed at fire;
      // a stale window (409) leaves it removed, a transient failure throws so the
      // pill-fire path restores the (still-eligible) pill.
      if (voiceCorrection) {
        const result = await API.setAdviceStance(sessionId, stance, {
          completeAttunement: false,
          voiceCorrection: true
        });
        const ok = !!(result && result.success !== false);
        if (!ok) {
          if (result?.code === 'CORRECTION_WINDOW_CLOSED') {
            // Window closed: the pill is gone and stays gone — show nothing
            // misleading, and do NOT throw (no pill restore).
            console.log('[ActionRouter] Voice-correction window closed; pill retired.');
            return;
          }
          // Transient failure — surface it and throw so executeAction reports
          // failure and the pill-fire path restores the still-eligible pill.
          throw new Error('Voice correction failed');
        }
        // Persisted: render the visible request (live-only; the new-voice
        // response replays it on reload), then send the silent continuation.
        this.orchestrator.chatManager?.displayUserMessage?.(data.label, {
          extraClasses: ['message--button-echo']
        });
        await this.orchestrator.handleUserMessage('[voice_selected]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, stance }
        });
        return;
      }

      // Update backend metadata — MUST complete before sending message
      const result = await API.setAdviceStance(sessionId, stance, { completeAttunement: true });
      if (!result) {
        console.error('[ActionRouter] set_stance: metadata update failed, aborting stance change');
        return;
      }

      if (routingFork) {
        // Routing/landing fork: do NOT post the button label as a visible user
        // message — send a silent system trigger so the agent simply responds in
        // the chosen voice (silent-trigger pattern).
        await this.orchestrator.handleUserMessage('[voice_selected]', {
          metadata: { source: 'button_click', silent: true, systemTrigger: true, stance }
        });
        return;
      }

      // Bridge use (advice-bridge-to-act): the visible "Let's get practical" bubble
      // now comes from the group's `echo` flag (single source — Phase E consolidation).
      // Seed the stance silently (systemTrigger) so the agent still receives the label
      // and responds in the new stance, with no duplicate user bubble live or on reload.
      await this.orchestrator.handleUserMessage(data.label, {
        metadata: { source: 'button_click', silent: true, systemTrigger: true, stance }
      });
    });

    // SEAMLESS_TRANSITIONS: User chose to stay in current mode (decline button)
    // Button interaction already recorded by executeAction(). CSS handles button collapse.
    this.register('continue_current', async (data) => {
      console.log('[ActionRouter] User chose to continue current mode:', data.label);
    });

    // Foundation → Advice bridge terminator. Dispatched by the backend `endBridge`
    // action tool so the bridge turn's tool loop can break cleanly without a
    // follow-up Claude round-trip. No-op on the frontend — the bridge message
    // and topic card have already streamed in by the time this fires.
    this.register('end_bridge', async () => {
      console.log('[ActionRouter] Foundation bridge turn ended');
    });

    // orientation_to_foundation — skip dashboard, launch Foundation path directly
    this.register('orientation_to_foundation', async () => {
      const { sessionManager } = await import('../core/SessionManager.js');
      const priorSessionId = sessionManager.currentSessionId;
      console.log('[OrientationClose:Frontend] orientation_to_foundation handler fired', {
        priorSessionId,
        step: 'handler_entry'
      });
      // Don't call endSession() — it emits session:ended which triggers returnToDashboard
      // and clears the chat. switchMode → startSession automatically closes the previous
      // advice session on the backend, keeping the chat window continuous.
      try {
        await this.orchestrator.switchMode('DISCOVERY', {
          pathId: 'foundation',
          fromOrientation: true,
          skipWelcome: true
        });
        console.log('[OrientationClose:Frontend] switchMode(DISCOVERY) returned', {
          priorSessionId,
          step: 'switchMode_returned'
        });
      } catch (err) {
        console.error('[OrientationClose:Frontend] switchMode(DISCOVERY) threw', {
          priorSessionId,
          step: 'switchMode_threw',
          error: err?.message,
          stack: err?.stack
        });
        throw err;
      }
      await this.orchestrator._enterDiscoveryNavigation('foundation');
      console.log('[OrientationClose:Frontend] orientation_to_foundation complete', {
        priorSessionId,
        step: 'handler_complete'
      });
    });

    // orientation_exit — return to dashboard after orientation advice session
    this.register('orientation_exit', async () => {
      const { sessionManager } = await import('../core/SessionManager.js');
      const priorSessionId = sessionManager.currentSessionId;
      console.log('[OrientationClose:Frontend] orientation_exit handler fired', {
        priorSessionId,
        step: 'handler_entry'
      });
      // End the session properly so it gets processed (summary, insights, etc.)
      const closed = await sessionManager.endSession();
      console.log('[OrientationClose:Frontend] sessionManager.endSession() returned', {
        priorSessionId,
        step: 'endSession_returned',
        closed
      });
      // Route to / so handleBaseRoute fires showDashboard +
      // dashboardManager.returnToDashboard via the standard route flow.
      page('/');
    });

    // orientation_stay_in_scope — "Discuss affection patterns" button from the
    // `orientation-offtopic-deflect` group. User chose to stay in the narrowly-
    // scoped orientation conversation after pivoting off-topic. Fire a silent
    // synthetic `[scope_continue]` so the agent re-poses the question that was
    // pending when the user pivoted. No backend mutation — entryType stays
    // 'orientation_advice'; the backend's `scopeGuardrailCycleCount` (set when
    // the deflection buttons were shown) rolls back the effective turn count.
    this.register('orientation_stay_in_scope', async () => {
      console.log('[ActionRouter] User chose to stay focused on affection patterns');
      await this.orchestrator.handleUserMessage('[scope_continue]', {
        metadata: {
          source: 'scope_continue',
          silent: true,
          systemTrigger: true,
          scopeContinue: true
        }
      });
    });

    // switch_orientation_scope — "Discuss something else" button from the
    // `orientation-offtopic-deflect` group. Downgrade the session in place:
    // backend flips `metadata.entryType` to 'normal' and clears the stale
    // practice gate, then we clear the frontend `_fromOrientationAdvice`
    // flag (so subsequent practice accept/reject behavior doesn't fire),
    // and fire a silent synthetic `[scope_switched]` so the next agent turn
    // (now on the vanilla advice path) emits the deterministic
    // SCOPE_SWITCHED_OPENING transition. Same session, same history — only
    // the metadata flips.
    this.register('switch_orientation_scope', async () => {
      console.log('[ActionRouter] Downgrading orientation scope to normal advice');

      const sessionId = this.orchestrator._getSessionId();
      if (!sessionId) {
        console.error('[ActionRouter] switch_orientation_scope: no active session');
        window.notificationManager?.showError('Could not switch conversation. Please try again.');
        return;
      }

      try {
        const result = await API.downgradeOrientationScope(sessionId);
        if (!result || !result.sessionId) {
          console.error('[ActionRouter] switch_orientation_scope: downgrade failed', result);
          window.notificationManager?.showError('Could not switch conversation. Please try again.');
          return;
        }

        // Clear the orientation-advice flag so practice accept/reject (and
        // any other orientation-only synthetic-message gates) don't fire on
        // later turns.
        this.orchestrator._fromOrientationAdvice = false;

        // Silent synthetic trigger for the agent's transition turn.
        await this.orchestrator.handleUserMessage('[scope_switched]', {
          metadata: {
            source: 'scope_switched',
            silent: true,
            systemTrigger: true,
            scopeSwitched: true
          }
        });
      } catch (error) {
        console.error('[ActionRouter] Error switching orientation scope:', error);
        window.notificationManager?.showError('Could not switch conversation. Please try again.');
      }
    });

    // start_note_compose — "Start the note" button from the
    // `note-compose-confirm` group (plan: advice-scoping-routing.md Phase R2).
    // Flip the live SCOPING session into the memo_compose entry server-side
    // FIRST, then fire a silent synthetic `[note_compose_confirmed]` so the
    // agent's next turn opens the memo draft directly from its own side — the
    // user never re-types. On a failed flip, surface the error and leave the
    // confirm buttons mounted for retry (never a silent no-op).
    this.register('start_note_compose', async () => {
      console.log('[ActionRouter] Starting note-compose flip from scoping');

      const sessionId = this.orchestrator._getSessionId();
      if (!sessionId) {
        console.error('[ActionRouter] start_note_compose: no active session');
        window.notificationManager?.showError('Could not start the note. Please try again.');
        return;
      }

      const result = await API.startNoteCompose(sessionId);
      if (!result || result.success === false) {
        console.error('[ActionRouter] start_note_compose: flip failed', result);
        window.notificationManager?.showError(
          (typeof result?.error === 'string' && result.error) || 'Could not start the note. Please try again.'
        );
        return;
      }

      // Live header sync: AdviceMode.entryType is otherwise only set in
      // onEnter/onRestore, neither of which re-runs for an in-place flip — the
      // flip result is already known here (this request made it), so update
      // the cached field directly and re-render rather than round-tripping a
      // backend signal through the next turn's completion payload.
      const adviceMode = this.orchestrator.currentMode;
      if (adviceMode?.name === 'ADVICE' && typeof adviceMode.setEntryType === 'function') {
        adviceMode.setEntryType('memo_compose');
        window.headerManager?.setModeLabel(adviceMode.getModeLabel());
      }

      await this.orchestrator.handleUserMessage('[note_compose_confirmed]', {
        metadata: {
          source: 'note_compose_confirmed',
          silent: true,
          systemTrigger: true,
          noteComposeConfirmed: true
        }
      });
    });

    // decline_note_compose — "Just talk" button from the same group. Records
    // the durable decline (the offer never re-fires this session) and
    // continues the scoping conversation — no session mutation, no mode change.
    this.register('decline_note_compose', async () => {
      console.log('[ActionRouter] Declining note-compose offer');

      const sessionId = this.orchestrator._getSessionId();
      if (!sessionId) {
        console.error('[ActionRouter] decline_note_compose: no active session');
        return;
      }

      await API.updateSessionMetadata(sessionId, { noteComposeDeclined: true });

      await this.orchestrator.handleUserMessage('[note_compose_declined]', {
        metadata: {
          source: 'note_compose_declined',
          silent: true,
          systemTrigger: true,
          noteComposeDeclined: true
        }
      });
    });

    // Trigger confetti celebration effect
    this.register('trigger_confetti', async () => {
      console.log('[ActionRouter] Triggering confetti celebration');

      try {
        const confetti = (await import('../vendor/confetti.js')).default;

        // Single burst from center of screen
        confetti({
          particleCount: 100,
          spread: 100,
          startVelocity: 40,
          ticks: 120,           // ~2 seconds at 60fps
          colors: ['#3B82F6', '#F97316', '#EF4444'],  // blue, orange, red
          origin: { x: 0.5, y: 0.5 }
        });
      } catch (error) {
        // Graceful degradation - celebration continues without confetti
        console.error('[ActionRouter] Confetti error:', error);
      }
    });

    // ============================================================
    // SESSION TOPIC TRANSITION HANDLERS
    // ============================================================

    // Start a new topic - closes current session and creates fresh ADVICE session.
    // Routes through the canonical switchMode lifecycle (NOT a direct startSession
    // side door) so the mode/header/input/_treatmentSubMode all transition properly
    // — critical when the origin is TREATMENT:build. The prior transcript is
    // preserved (no clearMessages); a "Discussing New Topic" divider marks the
    // boundary; the LLM produces a warm transition opener from <transition_context>.
    this.register('new_topic', async () => {
      console.log('[ActionRouter] Starting new topic (topic transition)');

      const currentSessionId = this.orchestrator._getSessionId();

      try {
        // Front door: run the full lifecycle (onExit/onEnter, _treatmentSubMode
        // reset, header/input switch). force:true allows same-mode (ADVICE→ADVICE)
        // re-entry; skipWelcome:true suppresses the generic welcome. We deliberately
        // do NOT pass topicTransition/newSession — those would fire the early
        // _showModeTransitionFeedback divider (before the user bubble); we render
        // our own divider after the bubble instead.
        await this.orchestrator.switchMode('ADVICE', {
          force: true,
          skipWelcome: true,
          entryType: 'topic_transition',
          previousSessionId: currentSessionId
        });

        // Synthetic user bubble (handler-owned) → boundary divider → LLM transition
        // reply. silent:true so handleUserMessage sends to the backend without a
        // second bubble. displayUserMessage + addDividerMessage are synchronous, so
        // the on-screen order is guaranteed: bubble → divider → assistant.
        const opener = "Let's discuss a new topic.";
        this.orchestrator.chatManager.displayUserMessage(opener);
        this.orchestrator.chatManager.messageList.addDividerMessage('Discussing New Topic');
        await this.orchestrator.handleUserMessage(opener, {
          metadata: { source: 'topic_transition', silent: true }
        });

        console.log('[ActionRouter] New topic session created:', this.orchestrator._getSessionId());

      } catch (error) {
        // switchMode already surfaces a user-facing system message and re-throws on
        // create failure; the throw short-circuits the bubble/divider/send above.
        // Just log here — do NOT double-notify.
        console.error('[ActionRouter] Error starting new topic:', error);
      }
    });

    // Wrap up session - closes current session and returns to dashboard
    this.register('wrap_up', async () => {
      console.log('[ActionRouter] Wrapping up session');

      const currentSessionId = this.orchestrator._getSessionId();

      if (!currentSessionId) {
        console.warn('[ActionRouter] No session to wrap up');
        this.orchestrator.returnToDashboard();
        return;
      }

      try {
        // Import sessionManager to properly clear state
        const { sessionManager } = await import('../core/SessionManager.js');

        // Close the current session (triggers post-processing)
        const result = await API.closeSession(currentSessionId);

        if (!result?.closed) {
          console.error('[ActionRouter] Failed to close session');
          window.notificationManager?.showError('Failed to end session. Please try again.');
          return;
        }

        console.log('[ActionRouter] Session closed:', {
          sessionId: currentSessionId,
          processingQueued: result.processingQueued
        });

        // Clear session state in sessionManager
        sessionManager.currentSessionId = null;
        sessionManager.currentMode = null;
        sessionManager.sessionNumber = null;

        // Mode/chat/header teardown, then route to the dashboard.
        this.orchestrator.returnToDashboard();
        page('/');

      } catch (error) {
        console.error('[ActionRouter] Error wrapping up session:', error);
        window.notificationManager?.showError('Failed to end session. Please try again.');
      }
    });
  }

  /**
   * Fire the post-memo-completion wake turn. Outside memo_entry sessions this
   * is the existing cascade-advance/ack signal (memoDone); inside memo_entry
   * sessions (D8: never restart the wrap-up cascade at turn ~2) it fires a
   * cascade-inert acknowledgment-only turn instead, keyed by outcome.
   * @param {'sent'|'discarded'} outcome - Required, no default
   * @private
   * @returns {Promise<void>}
   */
  async _fireMemoDoneWake(outcome) {
    if (!['sent', 'discarded'].includes(outcome)) {
      throw new TypeError(`[ActionRouter] Invalid memo completion outcome: ${outcome}`);
    }
    if (this.orchestrator.currentMode?.name !== 'ADVICE') return;
    const memoEntry = this.orchestrator._memoEntrySession;
    try {
      await this.orchestrator.handleUserMessage('[memo_done]', {
        metadata: {
          source: 'button_click',
          silent: true,
          systemTrigger: true,
          skipCounting: true,
          memoDone: !memoEntry,
          memoEntryAckOnly: memoEntry ? outcome : undefined
        }
      });
    } catch (err) {
      if (memoEntry) {
        window.notificationManager?.showInfo?.('Noted');
        // A terminus reply now advances to the conclusion group; a lost ack wake
        // recovers on resume via the backend adviceInputRearm projection (the memo
        // resolution is durable). The toast is the immediate fallback.
        console.warn('[ActionRouter] memo_entry ack wake failed (resume recovers the conclusion — showed fallback toast):', err);
      } else {
        console.warn('[ActionRouter] memo_done wake failed (cascade self-heals):', err);
      }
    }
  }

  /**
   * Stream a programmatic intake-closing message with staggered fade-in,
   * then render the specified button group. Used by the close_intake handler
   * to stream the post-intake handoff body and render the paths-forward
   * buttons.
   *
   * @private
   * @param {string} messageText - Full message text to stream
   * @param {string} groupId - Button group ID to render after streaming
   * @param {Object} [opts]
   * @param {(buttons: Array) => Array} [opts.parameterize] - Optional transform
   *   applied to the group's buttons before rendering.
   */
  async _renderIntakeClosing(messageText, groupId, opts = {}) {
    const chatManager = this.orchestrator.chatManager;

    // Brief perceptual delay with thinking indicator so the transition
    // doesn't feel instant.
    chatManager.activateThinkingIndicator();
    await new Promise(resolve => setTimeout(resolve, 300));

    await chatManager.streamProgrammaticMessage(messageText, {
      chunkDelay: 400,
      messageType: 'system'
    });

    const group = window.buttonGroupRegistry?.get(groupId);
    if (group && window.actionButtons) {
      const buttons = opts.parameterize ? opts.parameterize(group.buttons) : group.buttons;
      const buttonGroup = window.actionButtons.createButtonGroup(buttons, { groupId });
      chatManager.messageList.addInteractiveComponent(buttonGroup, {
        type: 'action-buttons',
        data: { groupId }
      });
    }
  }

  /**
   * Register a new action handler
   * @param {string} actionType - Action type (e.g., 'switch_mode', 'load_next_path', 'navigate')
   * @param {Function} handler - Async handler function
   */
  register(actionType, handler) {
    this.handlers.set(actionType, handler);
  }

  /**
   * Unregister an action handler
   * @param {string} actionType - Action type to unregister
   */
  unregister(actionType) {
    this.handlers.delete(actionType);
  }

  /**
   * Re-arm an input-mounted button group without recording a synthetic user interaction.
   * Used when durable proposal state is restored after a page reload.
   *
   * @param {string} groupId - Registered button-group identifier
   * @param {Object} [input={}] - Dynamic handler input such as practiceId
   * @returns {Promise<boolean>} Whether the group handler was invoked
   */
  async rearmButtonGroup(groupId, input = {}) {
    try {
      const registry = window.buttonGroupRegistry;
      if (!registry?.loaded) {
        const loaded = await registry?.load();
        if (!loaded) {
          console.warn('[ActionRouter] Button group re-arm skipped: registry failed to load', groupId);
          return false;
        }
      }

      const handler = this.handlers.get('show_buttons');
      if (!handler) {
        console.warn('[ActionRouter] Button group re-arm skipped: show_buttons handler unavailable', groupId);
        return false;
      }

      await handler({ action: 'show_buttons', input: { ...input, groupId } });
      return true;
    } catch (error) {
      console.warn('[ActionRouter] Button group re-arm failed:', groupId, error);
      return false;
    }
  }

  /**
   * Register agent-surfaced `pill-row` groups as Tier-2 pills in the floating PillRow
   * (chat-input-redesign Phase 3). Shared by the `show_buttons` live surface and
   * `ModeOrchestrator.resumeSession` hydration. Resolves each group's button(s) from the
   * registry, wires the standard onClick (below), and replaces the current agent-pill set
   * (single Phase-3 tenant — replace is the correct idempotent semantic).
   *
   * @param {Array<string>} groupIds - pill-row button group ids (from metadata.activePills)
   */
  syncPillRowFromGroups(groupIds = []) {
    const pillRow = window.app?.modules?.pillRow;
    if (!pillRow?.setAgentPills) return;

    const pills = [];
    for (const groupId of Array.isArray(groupIds) ? groupIds : []) {
      const group = window.buttonGroupRegistry?.get(groupId);
      if (!group || group.renderLocation !== 'pill-row' || !Array.isArray(group.buttons)) continue;
      for (const button of group.buttons) {
        pills.push(this._buildAgentPill(button, groupId));
      }
    }
    pillRow.setAgentPills(pills);
  }

  /**
   * Terminal fallback for a proposal decision that matched no continuation
   * branch. The accept/reject handlers are if/else-if ladders over mode and
   * orchestrator flags; before this existed, an unmatched click succeeded
   * server-side and then did nothing at all — no marker, no turn, no log. That
   * silent fall-through is the 2026-07-27 production dead-end, and it is also
   * reachable from a stale card clicked after a mode switch.
   *
   * Gated on mode rather than firing unconditionally. In TREATMENT the marker
   * is safe and correct: both check-in and build route `[practice_accepted]` /
   * `[practice_rejected]` on marker presence alone, so the landing turn lands.
   * Outside TREATMENT there is no consumer — the literal bracket string would
   * reach an unrelated strategy as real turn content, producing a confused
   * visible reply and burning an LLM call. There we fail loudly instead.
   *
   * @param {'accept'|'reject'} decision - Which handler fell through
   * @param {string} practiceId - The practice the click targeted
   * @private
   */
  _handleUnroutedProposalDecision(decision, practiceId) {
    const marker = decision === 'accept' ? '[practice_accepted]' : '[practice_rejected]';
    const inTreatment = this.orchestrator.currentMode?.name === 'TREATMENT';

    console.error(
      `[ActionRouter] ${decision}_practice matched no continuation branch`,
      {
        practiceId,
        mode: this.orchestrator.currentMode?.name ?? null,
        subMode: this.orchestrator.currentMode?.subMode ?? null,
        willFireMarker: inTreatment
      }
    );

    if (!inTreatment) {
      // Deliberately does NOT mark the card stale: the mutation SUCCEEDED, and
      // "stale" means "no longer available". (Reject already marks its own card
      // before the branch chain, because its draft really is gone.) All that is
      // missing is the conversational acknowledgment, so say where it went.
      window.notificationManager?.showInfo(
        'That experiment card is from an earlier conversation — open it from Experiments to continue.'
      );
      return;
    }

    // The mutation already committed; a marker failure must not imply otherwise.
    this.orchestrator.handleUserMessage(marker, {
      metadata: {
        source: 'button_click',
        silent: true,
        systemTrigger: true,
        skipCounting: true,
        practiceId,
        ...(decision === 'accept' ? { practiceAccepted: true } : { practiceRejected: true })
      }
    }).catch(markerError => {
      console.error('[ActionRouter] unrouted decision fallback marker failed:', markerError);
    });
  }

  /**
   * Build a single agent-pill definition for the PillRow. The onClick routes through
   * `PillDispatch` (queue-until-idle: a click during streaming is queued and fires on
   * `chat:idle`; idle clicks fire immediately — see pill-streaming-interaction-design.md).
   *
   * The FIRE step (not the click) is where the pill leaves the row: it animates the
   * pill out and executes the action through `executeAction`. There is NO pre-action
   * durable consume anymore — retirement is server-authoritative at the acceptance
   * boundary (advice-unified-terminus §3.3). On `executeAction` returning `false`
   * (a pre-acceptance failure) the pill is restored to standing locally; a
   * post-acceptance failure returns non-false, so the accepted obligation is not
   * undone by restoring a consumed pill.
   *
   * @param {{id:string,label:string,action:string}} button - Button def from the group
   * @param {string} groupId - Owning group id (the `activePills` dedup key)
   * @returns {{id:string,label:string,groupId:string,onClick:Function}} PillRow pill def
   * @private
   */
  _buildAgentPill(button, groupId) {
    const fire = () => {
      // The pill leaves the row when its action actually fires (fired state).
      // NO pre-action durable consume (advice-unified-terminus §3.3): retirement
      // is now server-authoritative at the acceptance boundary — the button-
      // interaction transaction for artifact pills, the set-stance route for
      // correction pills — so a pre-consume/self-send partial failure can no
      // longer leave the pill consumed-but-unaccepted.
      window.app?.modules?.pillRow?.removePill?.(button.id, { animate: true });
      // Execute directly (same call the `action:button-click` listener makes) so the
      // outcome is observable. Passes the OWNING `groupId` (not `button.id`) so the
      // recorded interaction + telemetry carry the correct group, and forwards the
      // button's `data` payload (e.g. voice-correction's `{ voiceCorrection:true }`)
      // — pill-row buttons never traverse ActionButtons' dataset round-trip, so this
      // is the only place their `data` reaches the handler.
      this.executeAction(
        button.id,
        { action: button.action, label: button.label, groupId, ...(button.data && { data: button.data }) },
        { source: 'button-click' }
      ).then((ok) => {
        if (ok !== false) return;
        const liveGroupIds = (window.app?.modules?.pillRow?.agentPills || [])
          .map((pill) => pill.groupId)
          .filter(Boolean);
        this.syncPillRowFromGroups([...new Set([...liveGroupIds, groupId])]);
      }).catch(() => { /* executeAction never rejects; defensive */ });
    };

    return {
      id: button.id,
      label: button.label,
      groupId,
      onClick: () => this._dispatchOrFire(button.id, groupId, fire)
    };
  }

  /**
   * Route a click through `PillDispatch` (queue-until-idle), or fire it directly
   * when no dispatcher is mounted. Shared by the pill row and the chat-input
   * Create menu so both carry identical streaming semantics — a click during a
   * turn queues with the group's `queuedPlaceholder` and fires on `chat:idle`.
   *
   * @param {string} pillId - Button id (the dispatcher's pending-slot key)
   * @param {string} groupId - Owning button-group id
   * @param {Function} fire - Work to run at fire time
   * @param {string|null} [waitingText] - Overrides the group's `queuedPlaceholder`.
   *   Pill-row groups hold one button so group-level copy fits; the Create menu
   *   holds two with different copy, so it passes its button's own placeholder.
   * @returns {void}
   * @private
   */
  _dispatchOrFire(pillId, groupId, fire, waitingText = null) {
    const dispatch = window.app?.modules?.pillDispatch;
    if (!dispatch?.dispatch) {
      fire();
      return;
    }
    dispatch.dispatch({
      pillId,
      groupId,
      waitingText: waitingText || window.buttonGroupRegistry?.get(groupId)?.queuedPlaceholder || null,
      fire
    });
  }

  /**
   * Fire an `advice-create-menu` selection. The menu is not a pill row — it
   * mounts nothing and consumes nothing — so this only resolves the catalog
   * button and hands it to the same dispatch + `executeAction` path a pill uses.
   * From there the ordinary `compose_practice` / `generate_partner_memo` handlers
   * run, and because both are contract-bearing the
   * `POST /chat/button-interaction` record IS the acceptance boundary (it opens
   * the durable intent with `source: 'menu'` and re-checks eligibility).
   *
   * @param {string} item - Descriptor item key ('practice' | 'memo' | 'explore')
   * @returns {void}
   */
  dispatchCreateMenuItem(item) {
    const [entry] = resolveAdviceCreateMenuItems([item]);
    // The disabled check is LOAD-BEARING, not belt-and-braces: a local (unwired)
    // entry carries `id: null, action: null`, so letting it through would pass an
    // undefined pillId to _dispatchOrFire and toast "Invalid action" from
    // executeAction on any DOM/state desync. The rendered row's native `disabled`
    // attribute is the first defense; this is the dispatcher's own.
    if (!entry || entry.disabled) {
      if (!entry) console.warn('[ActionRouter] Unknown create-menu item:', item);
      return;
    }
    this._dispatchOrFire(entry.id, ADVICE_CREATE_MENU_GROUP_ID, () => {
      this.executeAction(
        entry.id,
        { action: entry.action, label: entry.label, groupId: ADVICE_CREATE_MENU_GROUP_ID },
        { source: 'button-click' }
      ).catch(() => { /* executeAction never rejects; defensive */ });
    }, entry.queuedPlaceholder);
  }

  /**
   * Execute an action
   * Records button interaction BEFORE executing action
   *
   * @param {string} actionId - Unique action ID (for logging/debugging)
   * @param {Object} actionData - Action data including action type, label, etc.
   * @returns {Promise<boolean|undefined>} `true` when the handler completed, `false`
   *   when it threw (already toasted here — callers may react, e.g. the pill fire
   *   path restores its pill), `undefined` on the early no-op paths (invalid data,
   *   unknown/retired action). Event-bus callers ignore the result.
   */
  async executeAction(actionId, actionData, { source = 'llm' } = {}) {
    // Validate action data
    if (!actionData || !actionData.action) {
      console.error('[ActionRouter] Invalid action data:', actionData);
      window.notificationManager?.showError('Invalid action');
      return;
    }

    const actionType = actionData.action.split(':')[0];
    const handler = this.handlers.get(actionType);

    if (!handler) {
      // Retired feature buttons live on in old transcripts forever — degrade
      // to a friendly notice instead of an error (see RETIRED_ACTIONS).
      if (RETIRED_ACTIONS.has(actionType)) {
        console.log(`[ActionRouter] Retired action clicked: ${actionType}`);
        window.notificationManager?.showInfo('This feature has been retired.');
        return;
      }
      console.error(`[ActionRouter] No handler registered for action: ${actionType}`);
      window.notificationManager?.showError('Unknown action type');
      return;
    }

    console.log(`[ActionRouter] Executing action: ${actionId} (${actionType})`);

    // Central in-input button teardown (source-gated). `executeAction` is invoked by
    // real button clicks, LLM-triggered actions, AND Discovery hybrid text-matches —
    // only user choices (`button-click`/`hybrid`) may tear the input region down, never
    // a synthetic `llm` action (which would nuke buttons just shown by the same turn).
    const input = window.app?.modules?.input;
    const userInitiated = source === 'button-click' || source === 'hybrid';
    // Snapshot the group that owns the input region (mounted OR stashed) at dispatch.
    const owner = userInitiated ? (input?.getOwningButtonGroupId?.() || null) : null;

    // Contract-bearing ADVICE artifact/decline actions (advice-unified-terminus
    // §3.3): the button-interaction record IS the durable acceptance boundary, so
    // a failed record must abort the handler and restore the pill. Every other
    // button keeps the lightweight fail-open recording. Resolved HERE (not at the
    // record below) because it also decides when the echo bubble may render.
    const isContractArtifact = this.orchestrator.currentMode?.name === 'ADVICE'
      && CONTRACT_BEARING_ADVICE_ACTIONS.has(actionType);

    // Echo the chosen button into the transcript as a user message, for groups flagged
    // `echo`. Direct clicks only — a hybrid match already showed the user's typed text.
    // A contract-bearing action DEFERS its echo until the interaction record commits:
    // its click can be refused (409 stale pick / stale menu item), and an echo already
    // in the transcript would assert a choice the server never accepted. Behaviour is
    // unchanged for every pre-existing member of that set — all of them are
    // SELF_SENDING_ACTIONS, so `_maybeEchoButtonClick` is a no-op for them either way.
    if (source === 'button-click') {
      if (!isContractArtifact) this._maybeEchoButtonClick(actionData);

      // Tear the input region down IMMEDIATELY on a direct click. One-choice-per-group
      // (ActionButtons.handleButtonClick) means a click always commits its group, so the
      // teardown is terminal — there is nothing to wait for. Deferring it to `finally`
      // (below) left the clicked buttons mounted for the full duration of a multi-second
      // handler (e.g. switch_mode awaits a new session + first streamed response), which
      // read as the buttons "sticking" in a clicked state. Hybrid/text-match dispatch
      // still tears down in `finally` so Discovery's typed-reply routing keeps the
      // active-buttons registry alive through the handler.
      this._teardownOwnedGroup(input, owner);
    }

    let accepted = false;

    try {
      // IMPORTANT: Record button interaction BEFORE executing action
      // This ensures user's intention is captured even if action fails
      if (isContractArtifact) {
        const record = await this.recordButtonInteraction(actionData);
        if (!record.ok) {
          // A Create-menu item the server no longer offers (409): the projection
          // the menu rendered from has moved on. Say so plainly and re-read it —
          // this is stale UI, not a failed action, and there is no pill or input
          // group to restore.
          if (record.code === CREATE_MENU_STALE_CODE) {
            window.notificationManager?.showInfo?.('That option is no longer available here.');
            await refreshAdviceCreateMenu(this.orchestrator._getSessionId());
            return false;
          }
          // A direction slot the server will no longer honour (409): the detour
          // window closed, the candidates moved, or a proposal is already live.
          // Recover onto the CLASSIC post-takeaway group — re-arming the
          // directions group would put the user straight back into the 409, and
          // re-reading `/info` would only re-derive the same dead group.
          if (record.code === DIRECTIONS_STALE_CODE) {
            window.notificationManager?.showInfo?.('That option is no longer available here');
            await this.rearmButtonGroup(classicPostTakeawayGroupId(), {});
            return false;
          }
          // Pre-acceptance failure: the obligation was never accepted → restore
          // the affordance and show the ordinary retry error.
          window.notificationManager?.showError('Action failed. Please try again.');
          // An INPUT-region group (e.g. the post-takeaway offers) was torn down by
          // the eager click teardown above, leaving a dead input. Re-mount it
          // with the input it was mounted with (memoized per group) so the retry
          // shows the SAME affordance — an empty input would drop the direction
          // slot count/labels and the practiceId. A PILL-ROW group is instead
          // restored by the pill-fire return-false path — do NOT double-restore
          // (pill-row groups never mount in the input).
          const owningGroup = actionData.groupId
            ? window.buttonGroupRegistry?.get(actionData.groupId)
            : null;
          if (owningGroup?.renderLocation === 'input') {
            await this.rearmButtonGroup(
              actionData.groupId,
              this._lastGroupInput.get(actionData.groupId) || {}
            );
          }
          return false;
        }
        accepted = true;
        // The record committed — the choice is real, so render its echo now.
        if (source === 'button-click') this._maybeEchoButtonClick(actionData);
      } else if (source !== 'llm') {
        await this.recordButtonInteraction(actionData);
      }
      // An `llm` dispatch is the model's own tool call, not a user choice, and
      // it arrives with no label and no groupId — recording it wrote a
      // `role:'user'` row reading `[User selected: Button clicked]` with
      // `buttonGroupId: null`. That row is redundant with the `[Action: …]`
      // `llm_action` row MessageOrchestrator already writes for every pending
      // action, and it is NOT filtered by `isTranscriptMessage`, so it leaked
      // into the live prompt plus summarization/extraction/session notes. It
      // also once defeated `hasGenuineUserEngagement` (abandoned sessions ran
      // full post-processing). `rearmButtonGroup` already routes around
      // `executeAction` for the same reason. Contract-bearing ADVICE actions
      // keep recording unconditionally above — their record IS the acceptance
      // boundary — though no LLM-triggerable action is currently in that set.
      // `hybrid` (Discovery text-match) still records: that IS a user choice.

      // Guest conversion gate. Deliberately placed HERE — after the interaction
      // was recorded and the group torn down, in place of running the handler —
      // so the one click the user made is recorded exactly once and the
      // continuation after conversion can skip straight to the handler.
      // Dismissal is what restores the torn-down group.
      if (await this._maybeOpenConversionGate(actionType, actionData)) {
        return true;
      }

      // Execute the action handler
      await handler(actionData);
      return true;

    } catch (error) {
      console.error(`[ActionRouter] Error executing action ${actionType}:`, error);
      window.notificationManager?.showError('Action failed. Please try again.');
      // A handler that armed the indicator and then threw leaves it shimmering
      // with no work behind it. Guarded on there being no live stream: handlers
      // like memo_refine_* arm and then await handleUserMessage, and a throw
      // downstream of that must not blank an indicator the in-flight turn owns
      // (its own MessageRenderer.finish() is the right teardown). Not in the
      // `finally` below — several handlers return normally while a downstream
      // await still owns the indicator.
      if (!this.orchestrator?.chatManager?.currentStream) {
        this.orchestrator?.chatManager?.deactivateThinkingIndicator?.();
      }
      // A post-acceptance artifact failure (the interaction already committed the
      // obligation server-side) must NOT restore the pill — recovery happens on
      // the next turn/resume. Return non-false so the pill-fire path leaves it
      // removed. Every other failure returns false to restore.
      return accepted ? true : false;
    } finally {
      // Terminal teardown for hybrid/text-match dispatch (a stashed text-fallback
      // owner is torn down here — hideButtons clears the stash). A direct button
      // click was already torn down eagerly above, so skip it here to avoid a
      // redundant second teardown.
      if (source !== 'button-click') {
        this._teardownOwnedGroup(input, owner);
      }
    }
  }

  /**
   * Open the account-creation overlay instead of running a guest-gated action.
   *
   * @param {string} actionType
   * @param {Object} actionData
   * @returns {Promise<boolean>} True if the gate opened (handler must not run)
   * @private
   */
  async _maybeOpenConversionGate(actionType, actionData) {
    if (window.app?.isGuest !== true) return false;
    const context = GUEST_CONVERSION_GATED_ACTIONS.get(actionType);
    if (!context) return false;

    console.log(`[ActionRouter] Guest conversion gate: ${actionType} (${context})`);
    await window.app.openConversionGate({
      action: actionType,
      context,
      groupId: actionData?.groupId || null,
      actionData
    });
    return true;
  }

  /**
   * Run an action's handler directly, with none of `executeAction`'s wrapper —
   * no echo, no interaction record, no group teardown.
   *
   * Used to continue a conversion-gated click after the account is created: all
   * of that already happened when the gate opened, and re-running it would
   * double-record a single user choice. Throws so the caller can degrade.
   *
   * @param {string} actionType
   * @param {Object} [actionData={}]
   * @returns {Promise<void>}
   */
  async runHandlerOnly(actionType, actionData = {}) {
    const handler = this.handlers.get(actionType);
    if (!handler) throw new Error(`No handler registered for continuation: ${actionType}`);
    console.log(`[ActionRouter] Continuation (handler-only): ${actionType}`);
    await handler(actionData);
  }

  /**
   * Tear down the input region iff `owner` still owns it (mounted OR stashed). Idempotent:
   * a no-op once the group has been hidden or replaced, so it is safe to call eagerly on
   * click AND again in `finally`.
   * @param {Object} input - window.app.modules.input
   * @param {string|null} owner - groupId snapshotted at dispatch
   * @private
   */
  _teardownOwnedGroup(input, owner) {
    if (owner && input?.getOwningButtonGroupId?.() === owner) {
      input.hideButtons({ groupId: owner });
      window.actionButtons?.clearActiveButtons?.(); // drop hybrid-routing registry (terminal)
    }
  }

  /**
   * Resolve a button group's echo behaviour from the catalog (per-button override
   * wins over the group-level flag).
   * @param {Object} actionData
   * @returns {{ echo: boolean, echoText: string|null }}
   * @private
   */
  _resolveEcho(actionData) {
    const groupId = actionData?.groupId;
    if (!groupId) return { echo: false, echoText: null };
    // Self-sending actions own their bubble (see SELF_SENDING_ACTIONS) — never echo.
    const actionType = (actionData.action || '').split(':')[0];
    if (SELF_SENDING_ACTIONS.has(actionType)) return { echo: false, echoText: null };
    const group = window.buttonGroupRegistry?.get?.(groupId);
    if (!group) return { echo: false, echoText: null };
    // Match the clicked button by action (exact, else by actionType prefix so
    // parameterized actions like `memo_send:<id>` still resolve).
    const buttons = group.buttons || [];
    const button =
      buttons.find(b => b.action === actionData.action) ||
      buttons.find(b => actionData.action?.startsWith(`${b.action}:`)) ||
      null;
    const echo = button?.echo ?? group.echo ?? false;
    const echoText = echo ? (button?.echoText || actionData.label || null) : null;
    return { echo: !!echo, echoText };
  }

  /**
   * Render the chosen button as a user-message bubble (presentational only — no
   * backend call, no LLM turn, no `userAuthored` stamp, so it does not count as
   * genuine engagement). Reload parity is handled via `recordButtonInteraction`
   * metadata + `ChatManager.loadHistoryMessages`.
   * @param {Object} actionData
   * @private
   */
  _maybeEchoButtonClick(actionData) {
    const { echo, echoText } = this._resolveEcho(actionData);
    if (!echo || !echoText) return;
    this.orchestrator.chatManager?.displayUserMessage?.(echoText, {
      extraClasses: ['message--button-echo']
    });
  }

  /**
   * Record button interaction as a message in the database. Called BEFORE action
   * execution to capture user intention. For contract-bearing ADVICE artifact/
   * decline actions this doubles as the durable acceptance boundary, so the
   * outcome is RETURNED (the caller aborts on failure for those, and branches on
   * the server's error `code`); every other button ignores it (fail-open).
   *
   * @param {Object} actionData - Action data with label, action, groupId
   * @returns {Promise<{ok: boolean, code: string|null}>} Whether the interaction
   *   was recorded server-side, plus the server's error code when it was not.
   * @private
   */
  async recordButtonInteraction(actionData) {
    const sessionId = this.orchestrator._getSessionId();
    if (!sessionId) {
      console.warn('[ActionRouter] No session ID, skipping button interaction recording');
      return { ok: true, code: null }; // no session → non-blocking (nothing to record)
    }

    try {
      // Stamp echo intent so history reload (ChatManager.loadHistoryMessages) can
      // render echo clicks as a natural user bubble and skip non-echo ones.
      const { echo, echoText } = this._resolveEcho(actionData);
      const result = await API.recordButtonInteraction(
        sessionId,
        actionData.label || 'Button clicked',
        actionData.action,
        actionData.groupId || null,
        {
          actionId: actionData.actionId,
          timestamp: Date.now(),
          echo,
          echoText
        }
      );

      // API returns the success data ({messageId,...}), an error envelope
      // ({success:false, error, code}), or null on a network error.
      const ok = !!(result && result.success !== false);
      if (!ok) {
        console.error('[ActionRouter] Button interaction record failed:', result);
        return { ok: false, code: result?.code || null };
      }

      console.log('[ActionRouter] Button interaction recorded:', {
        label: actionData.label,
        action: actionData.action
      });
      return { ok: true, code: null };

    } catch (error) {
      // Don't block action execution if recording fails
      console.error('[ActionRouter] Failed to record button interaction:', error);
      return { ok: false, code: null };
    }
  }
}
